import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { observer } from "mobx-react";
import { Redirect, useHistory, useLocation, useParams } from "react-router-dom";
import { Button, Col, Spinner, Card, Modal, Row, Form } from "react-bootstrap";

import HtmlHead from "components/html-head/HtmlHead";
import { GetEventJudgeStore } from "SessionState/Judge/EventJudgeStore";
import LayoutJudgePortal from "layout/LayoutJudgePortal";
import { JudgeDataContext } from "data/JudgeDataProvider";
import CsLineIcons from "cs-line-icons/CsLineIcons";
import JudgePortalShell from "./JudgePortalShell";
import { getAgeGroupName } from "helpers/PageantHelper";
import JudgeSideAwardWinners from "./JudgeSideAwardWinners";
import { LoadingOverlay } from "data/TabulationDataProvider";
import JudgeTabulatorHelpMessage from "./JudgeTabulatorHelpMessage";

type RouteParams = {
  eventJudgeId: string;
  eventId: string;
};

type ScoreFeedbackDraft = {
  excelledIn: string[];
  improveOn: string[];
  customNote: string;
  selectedPhoto?: number | null;
  deductionApplied?: boolean;
};

type ContestantAocStatus = {
  isEntered: boolean;
  isComplete: boolean;
  label: "COMPLETE" | "INCOMPLETE" | "SCORED" | "NOT ENTERED";
  badgeClass: string;
};

type FeedbackByKey = Record<string, ScoreFeedbackDraft>;

const makeFeedbackKey = (
  registeredPageantContestantId: number,
  aocId: number,
  judgeId: number,
) => `${registeredPageantContestantId}|${aocId}|${judgeId}`;

const JudgeScoringPlatform = observer(() => {
  const title = "Judge Portal";
  const description = "Judge Event Home";

  const { eventJudgeId, eventId } = useParams<RouteParams>();
  const judgeStore = GetEventJudgeStore();

  const [isReady, setIsReady] = useState(false);

  const parsedEventJudgeId = Number(eventJudgeId);
  const parsedEventId = Number(eventId);

  const judge = judgeStore.CurrentJudge;

  useEffect(() => {
    setIsReady(true);
  }, []);

  if (!isReady) {
    return (
      <>
        <HtmlHead title={title} description={description} image="" />
        <LoadingOverlay show={true} text={"Loading"} />
      
      </>
    );
  }

  if (!judge) {
    return <Redirect to="/judge/login" />;
  }

  if (judge.id !== parsedEventJudgeId || judge.eventId !== parsedEventId) {
    return <Redirect to="/judge/login" />;
  }

  return (
    <JudgePortalShell
      title="Judging"
      description="Judge Scoring Platform"
    >
      <JudgeScoringPlatformContent judge={judge} />
    </JudgePortalShell>
  );
});

type JudgeScoringPlatformProps = {
  judge: any;
};
type ViewMode = "ageGroupLanding" | "contestant" | "review" | "sideAwards";
type PickerMode = "aoc" | "division" | "ageGroup" | null;

type ConfirmModalState = {
  show: boolean;
  title: string;
  body: React.ReactNode;
  confirmText: string;
  cancelText: string;
  confirmVariant?: string;
};

type MissingScoreCategory = {
  id: number;
  name: string;
  category: any;
};

type MissingScoreModalState = {
  show: boolean;
  categories: MissingScoreCategory[];
};

type CompletionModalState = {
  show: boolean;
  type: "aoc" | "event";
  title: string;
  body: string;
};

const JudgeScoringPlatformContent: React.FC<JudgeScoringPlatformProps> = ({
  judge,
}) => {
  const {
    judgeId,
    pageant,
    pageantAvailableAOC,
    ageGroupsOrdered,
    eventDivisions,
    allFetchedScores,
    setAllFetchedScores,
    registeredContestants,
    refetchReceivedScores,
    isEnteredForAOC,
    receivedScores,
    setReceivedScores,
    saveScores,
    isLoading,
    pageantAvailableAwards,
    updateAwardRelationshipLocal,
    deleteScores
  } = useContext(JudgeDataContext);





  const location = useLocation();
  const history = useHistory();
  const { eventJudgeId: routeEventJudgeId, eventId: routeEventId } =
    useParams<RouteParams>();

  const judgeHomePath = useMemo(() => {
    const resolvedEventJudgeId = routeEventJudgeId || String(judge?.id ?? "");
    const resolvedEventId = routeEventId || String(judge?.eventId ?? "");
    return `/judge/eventhome/${resolvedEventJudgeId}/${resolvedEventId}`;
  }, [routeEventJudgeId, routeEventId, judge?.id, judge?.eventId]);

  const scoringDeepLinkParams = useMemo(() => {
    const params = new URLSearchParams(location.search);

    const parseNumberParam = (key: string): number | null => {
      const value = params.get(key);
      if (value == null || value === "") return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    return {
      aocId: parseNumberParam("aocId"),
      divisionId: parseNumberParam("divisionId"),
      ageGroupId: params.get("ageGroupId"),
      openFilters: params.get("openFilters") === "1",
    };
  }, [location.search]);

  const [selectedAOC, setSelectedAOC] = useState<number | null>(null);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<any | null>(null);
  const [divisionId, setDivisionId] = useState<number | null>(null);
  const [pickerMode, setPickerMode] = useState<PickerMode>(null);
  const [contestantIndex, setContestantIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  //==============SCORE DELETION STATE==============
  const [showFixMistakeModal, setShowFixMistakeModal] = useState(false);
const [selectedDeleteScoreIds, setSelectedDeleteScoreIds] = useState<number[]>([]);
const [isDeletingScores, setIsDeletingScores] = useState(false);
const [deleteConfirmArmed, setDeleteConfirmArmed] = useState(false);
const [showTabulatorMessageModal, setShowTabulatorMessageModal] = useState(false);
const [tabulatorMessageDraft, setTabulatorMessageDraft] = useState('');


  const [scoreTableSort, setScoreTableSort] = useState<{
    field: "number" | "rank";
    direction: "asc" | "desc";
  }>({
    field: "number",
    direction: "asc",
  });
  const [viewMode, setViewMode] = useState<ViewMode>("ageGroupLanding");

  const confirmResolverRef = useRef<((value: boolean) => void) | null>(null);
  const missingScoreResolverRef = useRef<((value: boolean) => void) | null>(
    null,
  );
  const promptedCompletedAocsRef = useRef<Set<number>>(new Set());
  const promptedEventCompleteRef = useRef(false);
  const hasAutoSelectedInitialRouteRef = useRef(false);
  const suppressExactCompletePromptRef = useRef(false);
  const userManuallySelectedAgeGroupRef = useRef(false);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    show: false,
    title: "",
    body: null,
    confirmText: "Continue",
    cancelText: "Cancel",
    confirmVariant: "primary",
  });
  const [missingScoreModal, setMissingScoreModal] =
    useState<MissingScoreModalState>({
      show: false,
      categories: [],
    });
  const [completionModal, setCompletionModal] = useState<CompletionModalState>({
    show: false,
    type: "aoc",
    title: "",
    body: "",
  });
  const [showContestantPicker, setShowContestantPicker] = useState(false);
  const [divisionCompleteModal, setDivisionCompleteModal] = useState(false);
  const [hasShownExactCompletePrompt, setHasShownExactCompletePrompt] =
    useState(false);

  const [updatedScores, setUpdatedScores] = useState<
    Record<number, Record<number, number>>
  >({});
  
  const [feedbackDrafts, setFeedbackDrafts] = useState<FeedbackByKey>({});
  const [sortDesc, setSortDesc] = useState(false);

  const [sideAwardHeaderSummary, setSideAwardHeaderSummary] = useState({
    total: 0,
    completed: 0,
    incomplete: 0,
    percent: 0,
  });

   const judgeStore =
    GetEventJudgeStore();

    

  const handleSideAwardSummaryChange = useCallback(
    (summary: { 
      total: number;
      completed: number;
      incomplete: number;
      percent: number;
    }) => {
      setSideAwardHeaderSummary((prev) => {
        const isSame =
          prev.total === summary.total &&
          prev.completed === summary.completed &&
          prev.incomplete === summary.incomplete &&
          prev.percent === summary.percent;

        if (isSame) return prev;

        // console.log("[SideAwards Parent] summary from child", summary);
        return summary;
      });
    },
    [],
  );



const [isHeaderCondensed, setIsHeaderCondensed] = useState(false);
const [isHeaderCondensedLocked, setIsHeaderCondensedLocked] = useState(false);

useEffect(() => {
  const getScrollTop = (): number => {
    const documentScrollTop =
      document.documentElement?.scrollTop ?? document.body?.scrollTop ?? 0;

    const scrollContainers = Array.from(
      document.querySelectorAll<HTMLElement>(
        ".scroll-out, .os-viewport, .content, main, [data-overlayscrollbars-viewport]",
      ),
    );

    const containerScrollTop = scrollContainers.reduce(
      (max, element) => Math.max(max, element.scrollTop || 0),
      0,
    );

    return Math.max(window.scrollY || 0, documentScrollTop, containerScrollTop);
  };

  const pageNeedsScroll = (): boolean => {
    return document.documentElement.scrollHeight > window.innerHeight + 8;
  };

  const updateHeaderState = () => {
    const scrollTop = getScrollTop();

    if (scrollTop <= 4) {
      setIsHeaderCondensed(false);
      setIsHeaderCondensedLocked(false);
      return;
    }

    if (scrollTop > 24 || isHeaderCondensedLocked) {
      setIsHeaderCondensed(true);

      window.requestAnimationFrame(() => {
        if (!pageNeedsScroll()) {
          setIsHeaderCondensedLocked(true);

          window.scrollTo({
            top: 0,
            behavior: "auto",
          });
        }
      });
    }
  };

  updateHeaderState();

  window.addEventListener("scroll", updateHeaderState, { passive: true });
  document.addEventListener("scroll", updateHeaderState, true);
  window.addEventListener("resize", updateHeaderState);

  const intervalId = window.setInterval(updateHeaderState, 250);

  return () => {
    window.removeEventListener("scroll", updateHeaderState);
    document.removeEventListener("scroll", updateHeaderState, true);
    window.removeEventListener("resize", updateHeaderState);
    window.clearInterval(intervalId);
  };
}, [isHeaderCondensedLocked]);

const scrollScoringViewToTop = useCallback(() => {
  window.requestAnimationFrame(() => {
    const scrollTargets = [
      window,
      document.documentElement,
      document.body,
      ...Array.from(
        document.querySelectorAll<HTMLElement>(
          ".scroll-out, .os-viewport, .content, main, [data-overlayscrollbars-viewport]",
        ),
      ),
    ];

    scrollTargets.forEach((target: any) => {
      if (target === window) {
        window.scrollTo({ top: 0, behavior: "auto" });
      } else if (target?.scrollTo) {
        target.scrollTo({ top: 0, behavior: "auto" });
      } else if ("scrollTop" in target) {
        target.scrollTop = 0;
      }
    });
  });
}, []);

  const togglePickerMode = useCallback((mode: PickerMode) => {
    setShowContestantPicker(false);
    setPickerMode(mode);
  }, []);

  const showConfirmModal = useCallback(
    (options: {
      title: string;
      body: React.ReactNode;
      confirmText?: string;
      cancelText?: string;
      confirmVariant?: string;
    }): Promise<boolean> => {
      return new Promise((resolve) => {
        confirmResolverRef.current = resolve;
        setConfirmModal({
          show: true,
          title: options.title,
          body: options.body,
          confirmText: options.confirmText ?? "Continue",
          cancelText: options.cancelText ?? "Cancel",
          confirmVariant: options.confirmVariant ?? "primary",
        });
      });
    },
    [],
  );

  const closeConfirmModal = useCallback((value: boolean) => {
    const resolver = confirmResolverRef.current;
    confirmResolverRef.current = null;
    setConfirmModal((prev) => ({ ...prev, show: false }));
    resolver?.(value);
  }, []);

  const showMissingScoreModal = useCallback(
    (categories: MissingScoreCategory[]): Promise<boolean> => {
      return new Promise((resolve) => {
        missingScoreResolverRef.current = resolve;
        setMissingScoreModal({ show: true, categories });
      });
    },
    [],
  );

  const closeMissingScoreModal = useCallback(
    (continueWithoutScore: boolean) => {
      const resolver = missingScoreResolverRef.current;
      missingScoreResolverRef.current = null;
      setMissingScoreModal({ show: false, categories: [] });
      resolver?.(continueWithoutScore);
    },
    [],
  );

  const getAocTypeLabel = useCallback((aoc: any): string => {
    const areaType = aoc?.areaOfCompetitionType;
    const aocType = aoc?.aocType;

    return String(
      (typeof areaType === "string" ? areaType : areaType?.name) ??
      aoc?.areaOfCompetitionTypeName ??
      aoc?.aocTypeName ??
      (typeof aocType === "string" ? aocType : aocType?.name) ??
      aoc?.typeName ??
      aoc?.categoryName ??
      "",
    )
      .trim()
      .toLowerCase();
  }, []);

  const getFeedbackVariantForAoc = useCallback(
    (aoc: any): "photo" | "stage" => {
      const searchable = [
        getAocTypeLabel(aoc),
        aoc?.name,
        aoc?.aocName,
        aoc?.areaOfCompetitionName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes("photo") ? "photo" : "stage";
    },
    [getAocTypeLabel],
  );

  const photoAocs = useMemo(() => {
    return (pageantAvailableAOC ?? []).filter((aoc: any) => {
      const typeLabel = getAocTypeLabel(aoc);
      return typeLabel.includes("photo");
    });
  }, [pageantAvailableAOC, getAocTypeLabel]);

  const stageAocs = useMemo(() => {
    return (pageantAvailableAOC ?? []).filter((aoc: any) => {
      const typeLabel = getAocTypeLabel(aoc);
      return typeLabel.includes("stage");
    });
  }, [pageantAvailableAOC, getAocTypeLabel]);

  const otherAocs = useMemo(() => {
    return (pageantAvailableAOC ?? []).filter((aoc: any) => {
      const typeLabel = getAocTypeLabel(aoc);
      return !typeLabel.includes("photo") && !typeLabel.includes("stage");
    });
  }, [pageantAvailableAOC, getAocTypeLabel]);

  const getDivisionIdValue = useCallback(
    (division: any): number =>
      Number(
        division?.divisionId ?? division?.id ?? division?.eventDivisionId ?? 0,
      ),
    [],
  );

  const getDivisionNameValue = useCallback(
    (division: any): string => division?.name ?? division?.divisionName ?? "",
    [],
  );

  const contestantNumberAsNum = useCallback((contestant: any): number => {
    const raw = contestant?.contestantNumber;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
  }, []);

  const formatScoreButtonLabel = useCallback((value: number): string => {
    const text = value.toFixed(1);
    return text.endsWith(".0") ? text.slice(0, -2) : text;
  }, []);

  const formatTrimmedScore = useCallback(
    (value: number | null | undefined): string => {
      if (value == null || Number.isNaN(Number(value))) return "0";
      const fixed = Number(value).toFixed(2);
      return fixed.replace(/\.?0+$/, "");
    },
    [],
  );

  const computeRanksWithTies = useCallback(
    (rows: Array<{ id: number; score: number }>) => {
      const sorted = [...rows].sort((a, b) => b.score - a.score);
      const rankMap = new Map<number, { rank: number; isTie: boolean }>();

      let currentRank = 1;

      for (let i = 0; i < sorted.length; i += 1) {
        if (i > 0 && sorted[i].score !== sorted[i - 1].score) {
          currentRank = i + 1;
        }

        const sameScoreCount = sorted.filter(
          (x) => x.score === sorted[i].score,
        ).length;

        rankMap.set(sorted[i].id, {
          rank: currentRank,
          isTie: sameScoreCount > 1,
        });
      }

      return rankMap;
    },
    [],
  );

  const selectedAocObj =
    pageantAvailableAOC.find(
      (a: any) => Number(a.id) === Number(selectedAOC),
    ) ?? null;

  const selectedDivisionObj =
    eventDivisions.find(
      (division: any) => getDivisionIdValue(division) === Number(divisionId),
    ) ?? null;

  const selectedDivisionName = getDivisionNameValue(selectedDivisionObj);

  const getAocCompetitionOrder = useCallback((aoc: any): number => {
    const raw =
      aoc?.competeOrder ??
      aoc?.competitionOrder ??
      aoc?.sortOrder ??
      aoc?.displayOrder ??
      aoc?.order ??
      Number.MAX_SAFE_INTEGER;

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
  }, []);

  const getDivisionCompetitionOrder = useCallback((division: any): number => {
    const raw =
      division?.competeOrder ??
      division?.competitionOrder ??
      division?.sortOrder ??
      division?.displayOrder ??
      division?.order ??
      Number.MAX_SAFE_INTEGER;

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
  }, []);

  const sortedPageantAvailableAOC = useMemo(() => {
    return [...(pageantAvailableAOC ?? [])].sort((a: any, b: any) => {
      const orderDelta = getAocCompetitionOrder(a) - getAocCompetitionOrder(b);
      if (orderDelta !== 0) return orderDelta;
      return String(a?.name ?? "").localeCompare(String(b?.name ?? ""));
    });
  }, [pageantAvailableAOC, getAocCompetitionOrder]);

  const sortedEventDivisions = useMemo(() => {
    return [...(eventDivisions ?? [])].sort((a: any, b: any) => {
      const orderDelta =
        getDivisionCompetitionOrder(a) - getDivisionCompetitionOrder(b);
      if (orderDelta !== 0) return orderDelta;
      return String(getDivisionNameValue(a)).localeCompare(
        String(getDivisionNameValue(b)),
      );
    });
  }, [eventDivisions, getDivisionCompetitionOrder, getDivisionNameValue]);

  const hasRealAgeGroups = (ageGroupsOrdered ?? []).length > 0;

    const getContestantIdValue = useCallback((contestant: any): number => {
    return Number(
      contestant?.id ??
      contestant?.registeredContestantId ??
      contestant?.registeredPageantContestantId ??
      0,
    );
  }, []);

  const getContestantDivisionIdValue = useCallback(
    (contestant: any): number => {
      return Number(
        contestant?.attributes?.eventDivisionId ??
        contestant?.contestantAttributes?.eventDivisionId ??
        contestant?.divisionId ??
        contestant?.division?.id ??
        0,
      );
    },
    [],
  );

const divisionBasedAgeGroups = useMemo(() => {
  if (hasRealAgeGroups) return [];

  return sortedEventDivisions
    .map((division: any) => {
      const currentDivisionId = getDivisionIdValue(division);
      const currentDivisionName = getDivisionNameValue(division);

      const divisionContestants = (registeredContestants ?? []).filter(
        (contestant: any) =>
          getContestantDivisionIdValue(contestant) === currentDivisionId,
      );

      if (!currentDivisionId || divisionContestants.length === 0) return null;

      return {
        id: -currentDivisionId,
        ageGroupId: -currentDivisionId,
        pageantAgeGroupId: -currentDivisionId,
        name: currentDivisionName,
        ageGroupName: currentDivisionName,
        label: currentDivisionName,
        isDivisionFallbackAgeGroup: true,
        divisionId: currentDivisionId,
        contestants: divisionContestants,
      };
    })
    .filter(Boolean);
}, [
  hasRealAgeGroups,
  sortedEventDivisions,
  registeredContestants,
  getDivisionIdValue,
  getDivisionNameValue,
  getContestantDivisionIdValue,
]);

const effectiveAgeGroups = useMemo(() => {
  return hasRealAgeGroups ? ageGroupsOrdered : divisionBasedAgeGroups;
}, [hasRealAgeGroups, ageGroupsOrdered, divisionBasedAgeGroups]);



  const getAgeGroupIdValue = useCallback((ageGroup: any): number => {
    return Number(
      ageGroup?.ageGroupId ?? ageGroup?.id ?? ageGroup?.pageantAgeGroupId ?? 0,
    );
  }, []);

  const getAgeGroupNameValue = useCallback((ageGroup: any): string => {
    return String(
      ageGroup?.ageGroupName ?? ageGroup?.name ?? ageGroup?.label ?? "",
    );
  }, []);

  const doesAgeGroupMatchParam = useCallback(
    (
      ageGroup: any,
      rawAgeGroupId: string | number | null | undefined,
    ): boolean => {
      if (rawAgeGroupId == null || rawAgeGroupId === "") return false;

      const raw = String(rawAgeGroupId);
      const ageGroupId = getAgeGroupIdValue(ageGroup);
      const ageGroupName = getAgeGroupNameValue(ageGroup);

      return (
        String(ageGroupId) === raw ||
        String(ageGroup?.id ?? "") === raw ||
        String(ageGroup?.ageGroupId ?? "") === raw ||
        String(ageGroupName) === raw
      );
    },
    [getAgeGroupIdValue, getAgeGroupNameValue],
  );

  const getContestantsForAgeGroupAndDivisionOrg = useCallback(
    (ageGroup: any, targetDivisionId: number): any[] => {
      return (ageGroup?.contestants ?? [])
        .filter((contestant: any) => {
          return (
            getContestantDivisionIdValue(contestant) ===
            Number(targetDivisionId)
          );
        })
        .map((contestant: any) => ({
          ...contestant,
          id: contestant?.id ?? contestant?.registeredContestantId,
          fullName:
            contestant?.fullName ??
            contestant?.contestantName ??
            `${contestant?.firstName ?? ""} ${contestant?.lastName ?? ""}`.trim(),
          firstName:
            contestant?.firstName ??
            contestant?.contestantName?.split(" ")?.[0] ??
            "",
          lastName:
            contestant?.lastName ??
            contestant?.contestantName?.split(" ")?.slice(1).join(" ") ??
            "",
          contestantAreasOfCompetitionDetails:
            contestant?.contestantAreasOfCompetitionDetails ??
            (contestant?.areasOfCompetition ?? []).map((aoc: any) => ({
              ...aoc,
              aocId: aoc?.aocId ?? aoc?.pageantAvailableEventId,
              isEntered: aoc?.isEntered ?? true,
            })),
        }))
        .sort(
          (a: any, b: any) =>
            contestantNumberAsNum(a) - contestantNumberAsNum(b),
        );
    },
    [getContestantDivisionIdValue, contestantNumberAsNum],
  );

  const getContestantsForAgeGroupAndDivision = useCallback(
  (ageGroup: any, targetDivisionId: number): any[] => {
    return (ageGroup?.contestants ?? [])
      .filter((contestant: any) => {
        return (
          getContestantDivisionIdValue(contestant) === Number(targetDivisionId)
        );
      })
      .map((contestant: any) => ({
        ...contestant,
        id:
          contestant?.id ??
          contestant?.registeredContestantId ??
          contestant?.registeredPageantContestantId,
        fullName:
          contestant?.fullName ??
          contestant?.contestantName ??
          `${contestant?.firstName ?? ""} ${contestant?.lastName ?? ""}`.trim(),
        firstName:
          contestant?.firstName ??
          contestant?.contestantName?.split(" ")?.[0] ??
          "",
        lastName:
          contestant?.lastName ??
          contestant?.contestantName?.split(" ")?.slice(1).join(" ") ??
          "",
        contestantAreasOfCompetitionDetails:
          contestant?.contestantAreasOfCompetitionDetails ??
          (contestant?.areasOfCompetition ?? []).map((aoc: any) => ({
            ...aoc,
            aocId: aoc?.aocId ?? aoc?.pageantAvailableEventId,
            isEntered: aoc?.isEntered ?? true,
          })),
      }))
      .sort(
        (a: any, b: any) =>
          contestantNumberAsNum(a) - contestantNumberAsNum(b),
      );
  },
  [getContestantDivisionIdValue, contestantNumberAsNum],
);

  const availableAgeGroupsOrg = useMemo(() => {
    if (!divisionId) return [];

    return ageGroupsOrdered.filter((group: any) => {
      const contestantsInGroup = group?.contestants ?? [];

      const matchingDivisionContestants = contestantsInGroup.filter(
        (contestant: any) => {
          const contestantDivisionId = Number(
            contestant?.attributes?.eventDivisionId ??
            contestant?.divisionId ??
            contestant?.division?.id ??
            0,
          );

          return contestantDivisionId === Number(divisionId);
        },
      );

      return matchingDivisionContestants.length > 0;
    });
  }, [ageGroupsOrdered, divisionId]);

const availableAgeGroups = useMemo(() => {
  if (!divisionId) return [];

  const effectiveAgeGroupsList: any[] = Array.isArray(effectiveAgeGroups)
    ? [...effectiveAgeGroups]
    : [];

  return effectiveAgeGroupsList.filter((group: any) => {
    if (group?.isDivisionFallbackAgeGroup) {
      return Number(group?.divisionId) === Number(divisionId);
    }

    const contestantsInGroup = group?.contestants ?? [];

    return contestantsInGroup.some((contestant: any) => {
      return getContestantDivisionIdValue(contestant) === Number(divisionId);
    });
  });
}, [effectiveAgeGroups, divisionId, getContestantDivisionIdValue]);

  const isContestantEnteredInAoc = useCallback(
    (contestant: any): boolean => {
      if (!selectedAOC) return false;

      const details =
        contestant?.contestantAreasOfCompetitionDetails ??
        contestant?.areasOfCompetition ??
        [];

      const aocDetail = details.find(
        (d: any) =>
          Number(d?.aocId ?? d?.pageantAvailableEventId) ===
          Number(selectedAOC),
      );

      return Boolean(aocDetail?.isEntered);
    },
    [selectedAOC],
  );

  const contestants = useMemo(() => {
    if (!selectedAgeGroup || !divisionId) return [];

    return getContestantsForAgeGroupAndDivision(
      selectedAgeGroup,
      Number(divisionId),
    );
  }, [selectedAgeGroup, divisionId, getContestantsForAgeGroupAndDivision]);

  useEffect(() => {
    if (!contestants.length) {
      setContestantIndex(0);
      return;
    }

    if (contestantIndex > contestants.length - 1) {
      const firstEnteredIndex = contestants.findIndex((contestant: any) =>
        isContestantEnteredInAoc(contestant),
      );

      setContestantIndex(firstEnteredIndex >= 0 ? firstEnteredIndex : 0);
    }
  }, [contestants, contestantIndex, isContestantEnteredInAoc]);

  const selectedContestant = contestants[contestantIndex] ?? null;

  const [isSavingSideAwardWinner, setIsSavingSideAwardWinner] = useState(false);



  const isContestantEnteredForAocId = useCallback(
    (contestant: any, aocId: number): boolean => {
      const details =
        contestant?.contestantAreasOfCompetitionDetails ??
        contestant?.areasOfCompetition ??
        [];

      const aocDetail = details.find(
        (d: any) =>
          Number(d?.aocId ?? d?.pageantAvailableEventId) === Number(aocId),
      );

      return Boolean(aocDetail?.isEntered);
    },
    [],
  );

  const getFetchedScoreForAoc = useCallback(
    (
      contestantId: number,
      categoryId: number,
      aocId: number,
    ): number | null => {
      const contestantRows = allFetchedScores?.[Number(contestantId)] ?? [];

      const row = contestantRows.find(
        (s: any) =>
          Number(s?.aocId ?? s?.pageantAvailableEventId) === Number(aocId) &&
          Number(s?.aocScoreCategoryId) === Number(categoryId) &&
          Number(s?.judgeId ?? s?.eventJudgeId) === Number(judgeId),
      );

      return row?.score ?? null;
    },
    [allFetchedScores, judgeId],
  );

const handleDeleteSelectedScores = useCallback(async () => {
  if (!selectedContestant || selectedDeleteScoreIds.length === 0) return;

  const confirmed = await showConfirmModal({
    title: "ARE YOU SURE?",
    body: (
      <div>
        <p className="mb-2">
          This will permanently delete the selected score(s).
        </p>
        <p className="text-danger fw-bold mb-0">
          This cannot be undone.
        </p>
      </div>
    ),
    confirmText: "Yes, Delete Scores",
    cancelText: "Cancel",
    confirmVariant: "danger",
  });

  if (!confirmed) return;

  try {
    setIsDeletingScores(true);

    await deleteScores(selectedDeleteScoreIds);

    const contestantId = Number(selectedContestant.id);

    setUpdatedScores((prev) => {
      const next = { ...prev };
      delete next[contestantId];
      return next;
    });

    if (selectedAOC && judgeId) {
  const feedbackKey = makeFeedbackKey(
    contestantId,
    Number(selectedAOC),
    Number(judgeId),
  );

  setFeedbackDrafts((prev) => {
    const next = { ...prev };
    delete next[feedbackKey];
    return next;
  });
}

    setSelectedDeleteScoreIds([]);
    setShowFixMistakeModal(false);
  } catch (error) {
    console.error("Error deleting selected scores:", error);
  } finally {
    setIsDeletingScores(false);
  }
}, [
  selectedContestant,
  selectedDeleteScoreIds,
  deleteScores,
  showConfirmModal,
]);

  const isContestantCompleteForAocObject = useCallback(
    (contestant: any, aoc: any): boolean => {
      const contestantId = getContestantIdValue(contestant);
      const aocId = Number(aoc?.id ?? 0);
      if (
        !contestantId ||
        !aocId ||
        !isContestantEnteredForAocId(contestant, aocId)
      )
        return false;

      const categories = aoc?.aocScoreCategories ?? [];
      if (categories.length === 0) return false;

      return categories.every((cat: any) => {
        const categoryId = Number(cat?.id ?? 0);
        const score = getFetchedScoreForAoc(contestantId, categoryId, aocId);
        return score !== null && score !== undefined;
      });
    },
    [getContestantIdValue, isContestantEnteredForAocId, getFetchedScoreForAoc],
  );

  const getScoreIdValue = useCallback((row: any): number => {
  return Number(
    row?.id ??
      row?.Id ??
      row?.receivedScoreId ??
      row?.ReceivedScoreId ??
      row?.receivedScoresId ??
      row?.scoreId ??
      row?.ScoreId ??
      0,
  );
}, []);

const getFetchedScore = useCallback(
  (contestantId: number, categoryId: number): number | null => {
    if (!selectedAOC) return null;

    const contestantRows = allFetchedScores?.[Number(contestantId)] ?? [];

    const matchingRows = contestantRows.filter(
      (s: any) =>
        Number(s?.aocId ?? s?.pageantAvailableEventId) === Number(selectedAOC) &&
        Number(s?.aocScoreCategoryId) === Number(categoryId) &&
        Number(s?.judgeId ?? s?.eventJudgeId) === Number(judgeId),
    );

    const row =
      matchingRows.find((s: any) => getScoreIdValue(s) > 0) ??
      matchingRows[0];

    return row?.score ?? null;
  },
  [allFetchedScores, selectedAOC, judgeId, getScoreIdValue],
);

  const getCurrentScore = useCallback(
    (contestantId: number, categoryId: number): number | null => {
      const edited = updatedScores[contestantId]?.[categoryId];
      if (edited !== undefined) return edited;
      return getFetchedScore(contestantId, categoryId);
    },
    [updatedScores, getFetchedScore],
  );

  const buildScoreOptions = useCallback(
    (minScore: number, maxScore: number): number[] => {
      const tenthsBelow = 7;
      const desiredStart = maxScore - tenthsBelow * 0.1;
      const start = Math.max(minScore, desiredStart);

      const values: number[] = [];
      for (
        let x = Math.round(start * 10);
        x <= Math.round(maxScore * 10);
        x += 1
      ) {
        values.push(x / 10);
      }

      return values.slice(-8);
    },
    [],
  );

  const handleFinishSideAwards =
  useCallback(
    async (): Promise<void> => {
      if (
        isSavingSideAwardWinner
      ) {
        return;
      }

      const pageantId =
        Number(
          (pageant as any)?.id ??
            (pageant as any)
              ?.pageantId ??
            0,
        );

      const currentDivisionId =
        Number(
          divisionId ?? 0,
        );

      const isDivisionFallback =
        selectedAgeGroup
          ?.isDivisionFallbackAgeGroup ===
        true;

      const currentAgeGroupId =
        isDivisionFallback
          ? null
          : Number(
              selectedAgeGroup
                ?.ageGroupId ??
                selectedAgeGroup?.id ??
                selectedAgeGroup
                  ?.pageantAgeGroupId ??
                0,
            ) || null;

      if (
        !pageantId ||
        !currentDivisionId
      ) {
        console.error(
          '[FinishSideAwards] Missing pageant or division.',
          {
            pageantId,
            divisionId:
              currentDivisionId,
            ageGroupId:
              currentAgeGroupId,
          },
        );

        return;
      }

      try {
        setIsSavingSideAwardWinner(
          true,
        );

        const complete =
          await judgeStore
            .finishSideAwards(
              pageantId,
              currentDivisionId,
              currentAgeGroupId,
            );

     if (!complete) {
  const leaveAnyway =
    await showConfirmModal({
      title:
        'Side Awards Incomplete',
      body: (
        <div>
          <p className="mb-2">
            One or more Side Awards
            still need at least one
            winner for this group.
          </p>

          <p className="mb-0 fw-bold">
            Do you want to leave
            Side Awards anyway?
          </p>
        </div>
      ),
      confirmText:
        'Leave Anyway',
      cancelText:
        'Go Back & Finish',
      confirmVariant:
        'warning',
    });

  if (!leaveAnyway) {
    return;
  }

  setViewMode(
    'ageGroupLanding',
  );

  return;
}

        // Only leave Side Awards after
        // backend confirms completion.
        setViewMode(
          'ageGroupLanding',
        );
      } catch (error) {
        console.error(
          '[FinishSideAwards] Error:',
          error,
        );
      } finally {
        setIsSavingSideAwardWinner(
          false,
        );
      }
    },
    [
      isSavingSideAwardWinner,
      pageant,
      divisionId,
      selectedAgeGroup,
      judgeStore,
      showConfirmModal,
    ],
  );

  // ============================ NOTES - start============================

  const FEEDBACK = {
    stage: {
      excel: [
        "Eye contact",
        "Smile",
        "Modeling",
        "Wardrobe fit",
        "Color choices",
        "Hair/Makeup",
      ],
      improve: [
        "Eye contact",
        "Smile",
        "Modeling",
        "Wardrobe fit",
        "Color choices",
        "Hair/Makeup",
      ],
    },
    photo: {
      excel: [
        "Facial Expression",
        "Focus/Clarity",
        "Lighting",
        "Wardrobe",
        "Theme",
      ],
      improve: [
        "Facial Expression",
        "Focus/Clarity",
        "Lighting",
        "Wardrobe",
        "Theme",
      ],
    },
  } as const;

  const FEEDBACK_ALIASES: Record<string, string> = {
    smile: "smile",
    modeling: "modeling",
    "wardrobe fit": "wardrobe fit",
    "color choices": "color choices",
    "hair/makeup": "hair/makeup",
    "eye contact": "eye contact",
    "facial expression": "facial expression",
    "focus/clarity": "focus/clarity",
    lighting: "lighting",
    wardrobe: "wardrobe",
    theme: "theme",
  };

  const canonicalFeedbackItem = (value: string): string => {
    const normalized = normalizeFeedbackItem(value);
    return FEEDBACK_ALIASES[normalized] ?? normalized;
  };

  const buildScoreNotesString = (draft?: ScoreFeedbackDraft): string => {
    if (!draft) return "";

    const parts: string[] = [];

    if (draft.selectedPhoto) {
      parts.push(`Selected Photo: ${draft.selectedPhoto}.`);
    }

    if (draft.excelledIn?.length) {
      parts.push(`Things you excelled in: ${draft.excelledIn.join(", ")}.`);
    }

    if (draft.improveOn?.length) {
      parts.push(`Things you could improve on: ${draft.improveOn.join(", ")}.`);
    }

    if (draft.deductionApplied) {
      parts.push("Deduction applied.");
    }

    const custom = draft.customNote?.trim();
    if (custom) parts.push(custom);

    return parts.join(" ");
  };

  const parseScoreNotesString = (notes?: string | null): ScoreFeedbackDraft => {
    const empty: ScoreFeedbackDraft = {
      excelledIn: [],
      improveOn: [],
      customNote: "",
      deductionApplied: false,
    };

    if (!notes || !notes.trim()) return empty;

    let remaining = notes.trim();

    const excelMatch = remaining.match(/Things you excelled in:\s*([^.]*)\./i);
    const improveMatch = remaining.match(
      /Things you could improve on:\s*([^.]*)\./i,
    );
    const deductionMatch = /Deduction applied\./i.test(remaining);

    const excelledIn = excelMatch?.[1]
      ? excelMatch[1]
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
      : [];

    const improveOn = improveMatch?.[1]
      ? improveMatch[1]
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
      : [];

    const selectedPhotoMatch = remaining.match(/Selected Photo:\s*([1-5])\./i);
    const selectedPhoto = selectedPhotoMatch?.[1]
      ? Number(selectedPhotoMatch[1])
      : null;

    remaining = remaining
      .replace(/Selected Photo:\s*[1-5]\./i, "")
      .replace(/Things you excelled in:\s*[^.]*\./i, "")
      .replace(/Things you could improve on:\s*[^.]*\./i, "")
      .replace(/Deduction applied\./gi, "")
      .trim();

    return {
      excelledIn,
      improveOn,
      selectedPhoto,
      deductionApplied: deductionMatch,
      customNote: remaining,
    };
  };

  const getFetchedNotes = useCallback(
    (contestantId: number): string => {
      if (!selectedAOC) return "";

      const contestantRows = allFetchedScores?.[Number(contestantId)] ?? [];

      const rowWithNotes = contestantRows.find(
        (s: any) =>
          Number(s?.aocId ?? s?.pageantAvailableEventId) ===
          Number(selectedAOC) &&
          Number(s?.judgeId ?? s?.eventJudgeId) === Number(judgeId) &&
          !!String(s?.notes ?? s?.Notes ?? "").trim(),
      );

      return String(rowWithNotes?.notes ?? "");
    },
    [allFetchedScores, selectedAOC, judgeId],
  );

  const normalizeFeedbackItem = (value: string): string =>
    String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/\.$/, "")
      .replace(/\s+/g, " ");

  const mapParsedItemsToOptions = (
    items: string[],
    options: readonly string[],
  ): string[] => {
    const optionMap = new Map(
      options.map((opt) => [canonicalFeedbackItem(opt), opt]),
    );

    return items
      .map((item) => optionMap.get(canonicalFeedbackItem(item)))
      .filter((item): item is string => Boolean(item));
  };

  // useEffect(() => {
  //   if (!selectedContestant || !selectedAOC || !judgeId) return;

  //   const feedbackKey = makeFeedbackKey(
  //     Number(selectedContestant.id),
  //     Number(selectedAOC),
  //     Number(judgeId),
  //   );

  //   const existingDraft = feedbackDrafts[feedbackKey];
  //   if (existingDraft) return;

  //   const fetchedNotes = getFetchedNotes(Number(selectedContestant.id));
  //   const parsed = parseScoreNotesString(fetchedNotes);

  //   const variant = getFeedbackVariantForAoc(selectedAocObj);
  //   const feedbackOptions = FEEDBACK[variant];

  //   const hydrated: ScoreFeedbackDraft = {
  //     selectedPhoto: parsed.selectedPhoto,
  //     excelledIn: mapParsedItemsToOptions(parsed.excelledIn, feedbackOptions.excel),
  //     improveOn: mapParsedItemsToOptions(parsed.improveOn, feedbackOptions.improve),
  //     deductionApplied: parsed.deductionApplied,
  //     customNote: parsed.customNote,
  //   };

  //   setFeedbackDrafts((prev) => ({
  //     ...prev,
  //     [feedbackKey]: hydrated,
  //   }));
  // }, [
  //   selectedContestant,
  //   selectedAOC,
  //   judgeId,
  //   getFetchedNotes,
  //   feedbackDrafts,
  //   selectedAocObj,
  //   getAocTypeLabel,
  // ]);

  // ============================ NOTES - end============================

  useEffect(() => {
  if (!selectedContestant || !selectedAOC || !judgeId) return;

  const contestantId = Number(selectedContestant.id);

  const feedbackKey = makeFeedbackKey(
    contestantId,
    Number(selectedAOC),
    Number(judgeId),
  );

  const fetchedNotes = getFetchedNotes(contestantId);
  const parsed = parseScoreNotesString(fetchedNotes);

  const variant = getFeedbackVariantForAoc(selectedAocObj);
  const feedbackOptions = FEEDBACK[variant];

  const hydrated: ScoreFeedbackDraft = {
    selectedPhoto: parsed.selectedPhoto,
    excelledIn: mapParsedItemsToOptions(parsed.excelledIn, feedbackOptions.excel),
    improveOn: mapParsedItemsToOptions(parsed.improveOn, feedbackOptions.improve),
    deductionApplied: parsed.deductionApplied,
    customNote: parsed.customNote,
  };

  setFeedbackDrafts((prev) => ({
    ...prev,
    [feedbackKey]: hydrated,
  }));
}, [
  selectedContestant,
  selectedAOC,
  judgeId,
  allFetchedScores,
  selectedAocObj,
  getFetchedNotes,
]);

  const handleScoreClick = useCallback(
    (contestantId: number, categoryId: number, score: number) => {
      setUpdatedScores((prev) => ({
        ...prev,
        [contestantId]: {
          ...(prev[contestantId] ?? {}),
          [categoryId]: score,
        },
      }));
    },
    [],
  );

  const prepareScoresForSaving = useCallback((): any[] => {
    if (!selectedContestant || !selectedAOC || !judgeId || !selectedAocObj)
      return [];

    const categories = selectedAocObj.aocScoreCategories ?? [];
    if (!categories.length) return [];

    const feedbackKey = makeFeedbackKey(
      Number(selectedContestant.id),
      Number(selectedAOC),
      Number(judgeId),
    );

    const noteString = buildScoreNotesString(feedbackDrafts[feedbackKey]);
    const firstCategoryId = Number(categories[0]?.id ?? 0);

    return categories
      .map((category: any) => {
        const categoryId = Number(category?.id ?? 0);

        const updatedScore = updatedScores[selectedContestant.id]?.[categoryId];
        const fetchedScore = getFetchedScore(selectedContestant.id, categoryId);

        const scoreWasEdited = updatedScore !== undefined;
        const shouldCarryNotes = categoryId === firstCategoryId && !!noteString;

        // Save edited score rows as usual
        if (scoreWasEdited) {
          return {
            registeredPageantContestantId: selectedContestant.id,
            aocScoreCategoryId: categoryId,
            judgeId,
            score: updatedScore,
            notes: shouldCarryNotes ? noteString : "",
          };
        }

        // If only notes changed, force-save the first category row
        if (
          shouldCarryNotes &&
          fetchedScore !== null &&
          fetchedScore !== undefined
        ) {
          return {
            registeredPageantContestantId: selectedContestant.id,
            aocScoreCategoryId: categoryId,
            judgeId,
            score: fetchedScore,
            notes: noteString,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [
    selectedContestant,
    selectedAOC,
    judgeId,
    selectedAocObj,
    updatedScores,
    feedbackDrafts,
    getFetchedScore,
  ]);

  const getMissingCategoriesForSelectedContestant =
    useCallback((): MissingScoreCategory[] => {
      if (!selectedContestant || !selectedAocObj) return [];

      return (selectedAocObj.aocScoreCategories ?? [])
        .filter((category: any) => {
          const categoryId = Number(category?.id ?? 0);
          const score = getCurrentScore(selectedContestant.id, categoryId);
          return score === null || score === undefined;
        })
        .map((category: any) => ({
          id: Number(category?.id ?? 0),
          name: String(category?.scoringCategoryName ?? "Unnamed Category"),
          category,
        }));
    }, [selectedContestant, selectedAocObj, getCurrentScore]);

  const hasChangedScoreEdits = useMemo(() => {
    if (!selectedContestant) return false;

    const editedScores = updatedScores[selectedContestant.id] ?? {};

    return Object.entries(editedScores).some(([categoryId, score]) => {
      const fetchedScore = getFetchedScore(
        selectedContestant.id,
        Number(categoryId),
      );

      if (fetchedScore === null || fetchedScore === undefined) return true;

      return Math.abs(Number(fetchedScore) - Number(score)) >= 0.0001;
    });
  }, [selectedContestant, updatedScores, getFetchedScore]);

  const hasNotesEdits = useMemo(() => {
    if (!selectedContestant || !selectedAOC || !judgeId) return false;

    const feedbackKey = makeFeedbackKey(
      Number(selectedContestant.id),
      Number(selectedAOC),
      Number(judgeId),
    );

    const draft = feedbackDrafts[feedbackKey];
    if (!draft) return false;

    const currentNotes = buildScoreNotesString(draft).trim();
    const fetchedNotes = getFetchedNotes(Number(selectedContestant.id)).trim();

    return currentNotes !== fetchedNotes;
  }, [
    selectedContestant,
    selectedAOC,
    judgeId,
    feedbackDrafts,
    getFetchedNotes,
  ]);

  const hasScoreEdits = hasChangedScoreEdits || hasNotesEdits;

  const getScoreUpdateSummary = useCallback((): string[] => {
    if (!selectedContestant || !selectedAocObj) return [];

    return (selectedAocObj.aocScoreCategories ?? [])
      .map((category: any) => {
        const categoryId = Number(category?.id ?? 0);
        const newScore = updatedScores[selectedContestant.id]?.[categoryId];

        if (newScore === undefined) return null;

        const previousScore = getFetchedScore(
          selectedContestant.id,
          categoryId,
        );
        if (previousScore === null || previousScore === undefined) return null;

        if (Math.abs(Number(previousScore) - Number(newScore)) < 0.0001)
          return null;

        return `${String(category?.scoringCategoryName ?? "Score")}: ${formatScoreButtonLabel(
          Number(previousScore),
        )} → ${formatScoreButtonLabel(Number(newScore))}`;
      })
      .filter((row: string | null): row is string => Boolean(row));
  }, [
    selectedContestant,
    selectedAocObj,
    updatedScores,
    getFetchedScore,
    formatScoreButtonLabel,
  ]);

  const confirmScoreUpdates = useCallback(async (): Promise<boolean> => {
    const changedRows = getScoreUpdateSummary();
    if (changedRows.length === 0) return true;

    const previewRows = changedRows.slice(0, 8);
    const extraCount = changedRows.length > 8 ? changedRows.length - 8 : 0;

    return showConfirmModal({
      title: "Confirm score changes",
      body: (
        <div>
          <p className="mb-2">
            You are making changes to previously saved scores.
          </p>
          <div className="border rounded bg-light p-3 small text-body">
            {previewRows.map((row) => (
              <div key={row}>{row}</div>
            ))}
            {extraCount > 0 && <div>...and {extraCount} more change(s)</div>}
          </div>
        </div>
      ),
      confirmText: "Save Changes",
      cancelText: "Go Back",
      confirmVariant: "warning",
    });
  }, [getScoreUpdateSummary, showConfirmModal]);

const handleSave = useCallback(
  async (options: { confirmUpdates?: boolean } = {}): Promise<boolean> => {
    const rows = prepareScoresForSaving();
    if (rows.length === 0) return true;

    if (options.confirmUpdates && !(await confirmScoreUpdates())) {
      return false;
    }

    try {
      setIsSaving(true);

      await saveScores(rows);

      const contestantId = Number(selectedContestant?.id ?? 0);
      const aocId = Number(selectedAOC ?? 0);

      setReceivedScores((prev: any[]) => {
        const next = [...(prev ?? [])];

        rows.forEach((savedRow: any) => {
          const normalizedRow = {
            ...savedRow,
            registeredPageantContestantId: contestantId,
            aocId,
          };

          const existingIndex = next.findIndex(
            (r: any) =>
              Number(
                r?.registeredPageantContestantId ?? r?.registeredContestantId,
              ) === contestantId &&
              Number(r?.aocScoreCategoryId) ===
                Number(normalizedRow?.aocScoreCategoryId) &&
              Number(r?.judgeId ?? r?.eventJudgeId) ===
                Number(normalizedRow?.judgeId),
          );

          if (existingIndex >= 0) {
            next[existingIndex] = {
              ...next[existingIndex],
              ...normalizedRow,
              id: getScoreIdValue(next[existingIndex]),
            };
          } else {
            next.push(normalizedRow);
          }
        });

        return next;
      });

      setAllFetchedScores((prev: any) => {
        const existingRows = [...(prev?.[contestantId] ?? [])];

        rows.forEach((savedRow: any) => {
          const normalizedRow = {
            ...savedRow,
            registeredPageantContestantId: contestantId,
            aocId,
          };

          const existingIndex = existingRows.findIndex(
            (r: any) =>
              Number(
                r?.registeredPageantContestantId ?? r?.registeredContestantId,
              ) === contestantId &&
              Number(r?.aocScoreCategoryId) ===
                Number(normalizedRow?.aocScoreCategoryId) &&
              Number(r?.judgeId ?? r?.eventJudgeId) ===
                Number(normalizedRow?.judgeId),
          );

          if (existingIndex >= 0) {
            existingRows[existingIndex] = {
              ...existingRows[existingIndex],
              ...normalizedRow,
              id: getScoreIdValue(existingRows[existingIndex]),
            };
          } else {
            existingRows.push(normalizedRow);
          }
        });

        return {
          ...prev,
          [contestantId]: existingRows,
        };
      });

      setUpdatedScores((prev) => {
        const next = { ...prev };
        delete next[contestantId];
        return next;
      });

      return true;
    } catch (error) {
      console.error("Error saving judge scores:", error);
      return false;
    } finally {
      setIsSaving(false);
    }
  },
  [
    prepareScoresForSaving,
    confirmScoreUpdates,
    saveScores,
    selectedContestant,
    selectedAOC,
    setReceivedScores,
    setAllFetchedScores,
    getScoreIdValue,
  ],
);

const handleOpenFixMistake = useCallback(async () => {
  const contestantId = Number(selectedContestant?.id ?? 0);

  setSelectedDeleteScoreIds([]);
  setDeleteConfirmArmed(false);

  if (contestantId) {
    await refetchReceivedScores({
      registeredPageantContestantId: contestantId,
    });
  }

  setShowFixMistakeModal(true);
}, [selectedContestant, refetchReceivedScores]);


        const hasNonDeletableScores =
  selectedAocObj?.aocScoreCategories?.some((category: any) => {
    const categoryId = Number(category?.id ?? 0);
    const contestantId = Number(selectedContestant?.id ?? 0);

    const matchingRows =
      allFetchedScores?.[contestantId]?.filter(
        (row: any) =>
          Number(row?.aocScoreCategoryId) === categoryId &&
          Number(row?.judgeId ?? row?.eventJudgeId) === Number(judgeId),
      ) ?? [];

    const scoreRow =
      [...matchingRows]
        .filter((row: any) => getScoreIdValue(row) > 0)
        .sort(
          (a: any, b: any) =>
            getScoreIdValue(b) - getScoreIdValue(a),
        )[0] ?? matchingRows[matchingRows.length - 1];

    const score = scoreRow?.score;
    const scoreId = getScoreIdValue(scoreRow);

    return score !== null && score !== undefined && scoreId <= 0;
  }) ?? false;

//========================TEST SCORE STUFF - start===========================

const TABLET_TEST_NOTE = 'TABLET TEST - DELETE ME';

const containsTabletTestNote = (value: any): boolean => {
  return String(value ?? '')
    .toUpperCase()
    .includes(TABLET_TEST_NOTE);
};

const hasTabletTestScoreOrNote = useMemo(() => {
  const fetchedHasTest = Object.values(allFetchedScores ?? {}).some((rows: any) =>
    (Array.isArray(rows) ? rows : []).some((row: any) => {
      return containsTabletTestNote(
        row?.notes ??
          row?.Notes ??
          row?.scoreNotes ??
          row?.ScoreNotes,
      );
    }),
  );

  const receivedHasTest = (receivedScores ?? []).some((row: any) => {
    return containsTabletTestNote(
      row?.notes ??
        row?.Notes ??
        row?.scoreNotes ??
        row?.ScoreNotes,
    );
  });

  const draftHasTest = Object.values(feedbackDrafts ?? {}).some((draft: any) => {
    return containsTabletTestNote(draft?.customNote);
  });

  const selectedFetchedNoteHasTest = selectedContestant
    ? containsTabletTestNote(getFetchedNotes(Number(selectedContestant.id)))
    : false;

  return (
    fetchedHasTest ||
    receivedHasTest ||
    draftHasTest ||
    selectedFetchedNoteHasTest
  );
}, [
  allFetchedScores,
  receivedScores,
  feedbackDrafts,
  selectedContestant,
  getFetchedNotes,
]);

//========================TEST SCORE STUFF - end===========================

  const openTabulatorMessageForFixMistake = () => {
  const contestantNumber =
    selectedContestant?.contestantNumber ??
    selectedContestant?.number ??
    selectedContestant?.attributes?.contestantNumber ??
    '—';

  const contestantName =
    selectedContestant?.fullName ??
    selectedContestant?.contestantName ??
    `${selectedContestant?.firstName ?? ''} ${selectedContestant?.lastName ?? ''}`.trim() ??
    'Unknown Contestant';

  const aocName =
    selectedAocObj?.name ??
    'Unknown Event';

  setTabulatorMessageDraft(
    `I need assistance deleting scores for contestant #${contestantNumber} ${contestantName} in ${aocName}. Some scores may still be syncing and cannot be deleted from the judge portal.`
  );

  setShowTabulatorMessageModal(true);
};

const fixMistakeUiModal = (
  <Modal
    show={showFixMistakeModal}
    onHide={() => {
      if (!isDeletingScores) {
        setShowFixMistakeModal(false);
        setSelectedDeleteScoreIds([]);
        setDeleteConfirmArmed(false);
      }
    }}
    centered
    animation={false}
  >
    <Modal.Header closeButton>
      <Modal.Title>Fix Mistake</Modal.Title>
    </Modal.Header>

    <Modal.Body>
      <p className="text-muted mb-3">
        Select the saved score(s) that need to be deleted.
      </p>

{hasNonDeletableScores && (
  <div className="alert alert-info py-2 mb-3">
    <div className="small mb-2">
      Please contact the tabulator for further assistance to adjust potential
      scoring issues.
    </div>

    <Button
      size="sm"
      variant="outline-primary"
      onClick={openTabulatorMessageForFixMistake}
      disabled={isDeletingScores}
    >
      Message Tabulator
    </Button>
  </div>
)}

<JudgeTabulatorHelpMessage
  show={showTabulatorMessageModal}
  onHide={() => setShowTabulatorMessageModal(false)}
  initialMessage={tabulatorMessageDraft}
/>

      {selectedAocObj?.aocScoreCategories?.map((category: any) => {
        const categoryId = Number(category?.id ?? 0);
        const contestantId = Number(selectedContestant?.id ?? 0);

        const matchingRows =
          allFetchedScores?.[contestantId]?.filter(
            (row: any) =>
              Number(row?.aocScoreCategoryId) === categoryId &&
              Number(row?.judgeId ?? row?.eventJudgeId) === Number(judgeId),
          ) ?? [];

        const scoreRow =
          [...matchingRows]
            .filter((row: any) => getScoreIdValue(row) > 0)
            .sort(
              (a: any, b: any) =>
                getScoreIdValue(b) - getScoreIdValue(a),
            )[0] ?? matchingRows[matchingRows.length - 1];

        const scoreId = getScoreIdValue(scoreRow);
        const score = scoreRow?.score;
        const scoreIdTest = scoreRow?.id;
        const canDelete = scoreId > 0;

       

        if (score === null || score === undefined) return null;


        return (
          <div key={categoryId} className="border rounded p-3 mb-2">
            <Form.Check
              type="checkbox"
              id={`delete-score-${categoryId}`}
              checked={canDelete && selectedDeleteScoreIds.includes(scoreId)}
              disabled={isDeletingScores || !canDelete}
              onChange={(event) => {
                if (!canDelete) return;

                const checked = event.currentTarget.checked;
                setDeleteConfirmArmed(false);

                setSelectedDeleteScoreIds((prev) =>
                  checked
                    ? [...prev, scoreId]
                    : prev.filter((id) => id !== scoreId),
                );
              }}
              label={
                <span>
                  <span className="fw-bold">
                    {category?.scoringCategoryName ?? "Score Category"}
                  </span>
                  <span className="text-muted ms-2">
                    Saved Score: {formatTrimmedScore(score)}
                  </span>

                  {!canDelete && (
                    <span className="text-warning ms-2">
                      Save is syncing...
                    </span>
                  )}
                </span>
              }
            />
          </div>
        );
      })}
    </Modal.Body>

    <Modal.Footer>
      <Button
        variant="outline-secondary"
        onClick={() => {
          setShowFixMistakeModal(false);
          setSelectedDeleteScoreIds([]);
          setDeleteConfirmArmed(false);
        }}
        disabled={isDeletingScores}
      >
        Cancel
      </Button>

      <Button
        variant={deleteConfirmArmed ? "danger" : "outline-danger"}
        disabled={selectedDeleteScoreIds.length === 0 || isDeletingScores}
        onClick={() => {
          if (!deleteConfirmArmed) {
            setDeleteConfirmArmed(true);
            return;
          }

          void handleDeleteSelectedScores();
        }}
      >
        {isDeletingScores
          ? "Deleting..."
          : deleteConfirmArmed
            ? "ARE YOU SURE?"
            : "DELETE SCORES"}
      </Button>
    </Modal.Footer>
  </Modal>
);

const confirmMissingScores = useCallback(async (): Promise<boolean> => {
  // Contestants not entered in the selected AOC can move on freely
  if (!selectedContestant || !isContestantEnteredInAoc(selectedContestant)) {
    return true;
  }

  const missingCategories = getMissingCategoriesForSelectedContestant();

  // No missing scores at all
  if (missingCategories.length === 0) {
    return true;
  }

  // Determine whether ANY scores have actually been entered yet
  const hasAnyScoresStarted = missingCategories.length <
    (selectedAocObj?.aocScoreCategories?.length ?? 0);

  // If literally zero scores entered, allow moving on without warning
  if (!hasAnyScoresStarted) {
    return true;
  }

  // Partial scoring only → show warning modal
  return showMissingScoreModal(missingCategories);
}, [
  selectedContestant,
  isContestantEnteredInAoc,
  getMissingCategoriesForSelectedContestant,
  selectedAocObj,
  showMissingScoreModal,
]);

  const handleUndoChanges = useCallback(async () => {
    if (!selectedContestant) return;

    const shouldUndo = await showConfirmModal({
      title: "Undo unsaved changes?",
      body: "This will remove all unsaved score and feedback edits for the current contestant.",
      confirmText: "Undo Changes",
      cancelText: "Keep Editing",
      confirmVariant: "danger",
    });

    if (!shouldUndo) return;

    setUpdatedScores((prev) => {
      const next = { ...prev };
      delete next[Number(selectedContestant.id)];
      return next;
    });

    if (selectedAOC && judgeId) {
      const feedbackKey = makeFeedbackKey(
        Number(selectedContestant.id),
        Number(selectedAOC),
        Number(judgeId),
      );

      setFeedbackDrafts((prev) => {
        const next = { ...prev };
        delete next[feedbackKey];
        return next;
      });
    }
  }, [selectedContestant, selectedAOC, judgeId, showConfirmModal]);

  const discardCurrentContestantEdits = useCallback(() => {
    if (!selectedContestant) return;

    setUpdatedScores((prev) => {
      const next = { ...prev };
      delete next[Number(selectedContestant.id)];
      return next;
    });

    if (selectedAOC && judgeId) {
      const feedbackKey = makeFeedbackKey(
        Number(selectedContestant.id),
        Number(selectedAOC),
        Number(judgeId),
      );

      setFeedbackDrafts((prev) => {
        const next = { ...prev };
        delete next[feedbackKey];
        return next;
      });
    }
  }, [selectedContestant, selectedAOC, judgeId]);

  const proceedWithUnsavedChanges = useCallback(
    async (
      proceed: () => void,
      options?: { autoSave?: boolean },
    ): Promise<void> => {
      if (!hasScoreEdits) {
        proceed();
        return;
      }

      if (options?.autoSave) {
        const saved = await handleSave({ confirmUpdates: true });
        if (saved) proceed();
        return;
      }

      const shouldSave = await showConfirmModal({
        title: "Unsaved changes",
        body: "You have unsaved changes. Save before continuing, or continue without saving these edits.",
        confirmText: "Save and Continue",
        cancelText: "Continue Without Saving",
        confirmVariant: "success",
      });

      if (shouldSave) {
        const saved = await handleSave({ confirmUpdates: true });
        if (saved) proceed();
        return;
      }

      discardCurrentContestantEdits();
      proceed();
    },
    [
      hasScoreEdits,
      handleSave,
      discardCurrentContestantEdits,
      showConfirmModal,
    ],
  );

  const isContestantComplete = useCallback(
    (contestantId: number): boolean => {
      if (!selectedAocObj) return false;

      const categories = selectedAocObj.aocScoreCategories ?? [];
      if (categories.length === 0) return false;

      return categories.every((cat: any) => {
        const categoryId = Number(cat?.id ?? 0);
        const score = getFetchedScore(contestantId, categoryId);
        return score !== null;
      });
    },
    [selectedAocObj, getFetchedScore],
  );

  const isContestantCompleteForSelectedAoc = useCallback(
    (contestant: any): boolean => {
      if (!selectedAOC || !selectedAocObj) return false;

      const contestantId = Number(
        contestant?.id ?? contestant?.registeredContestantId ?? 0,
      );
      if (!contestantId) return false;

      const details =
        contestant?.contestantAreasOfCompetitionDetails ??
        contestant?.areasOfCompetition ??
        [];

      const aocDetail = details.find(
        (d: any) =>
          Number(d?.aocId ?? d?.pageantAvailableEventId) ===
          Number(selectedAOC),
      );

      const entered = Boolean(aocDetail?.isEntered);
      if (!entered) return false;

      const categories = selectedAocObj.aocScoreCategories ?? [];
      if (categories.length === 0) return false;

      return categories.every((cat: any) => {
        const categoryId = Number(cat?.id ?? 0);
        const score = getCurrentScore(contestantId, categoryId);
        return score !== null && score !== undefined;
      });
    },
    [selectedAOC, selectedAocObj, getCurrentScore],
  );

  const getEnteredContestantsForAgeGroup = useCallback(
    (ageGroup: any): any[] => {
      const contestantsInGroup = (ageGroup?.contestants ?? []).filter(
        (contestant: any) => {
          const contestantDivisionId = Number(
            contestant?.attributes?.eventDivisionId ??
            contestant?.divisionId ??
            contestant?.division?.id ??
            0,
          );

          return contestantDivisionId === Number(divisionId);
        },
      );

      return contestantsInGroup.filter((contestant: any) => {
        const details =
          contestant?.contestantAreasOfCompetitionDetails ??
          contestant?.areasOfCompetition ??
          [];

        const aocDetail = details.find(
          (d: any) =>
            Number(d?.aocId ?? d?.pageantAvailableEventId) ===
            Number(selectedAOC),
        );

        return Boolean(aocDetail?.isEntered);
      });
    },
    [divisionId, selectedAOC],
  );

  const isAgeGroupCompleteForEnteredContestants = useCallback(
    (ageGroup: any): boolean => {
      const enteredContestants = getEnteredContestantsForAgeGroup(ageGroup);
      if (enteredContestants.length === 0) return false;

      return enteredContestants.every((contestant: any) =>
        isContestantCompleteForSelectedAoc(contestant),
      );
    },
    [getEnteredContestantsForAgeGroup, isContestantCompleteForSelectedAoc],
  );

  const getAgeGroupCompletionCounts = useCallback(
    (ageGroup: any): { complete: number; incomplete: number } => {
      const enteredContestants = getEnteredContestantsForAgeGroup(ageGroup);

      const complete = enteredContestants.filter((contestant: any) =>
        isContestantCompleteForSelectedAoc(contestant),
      ).length;

      return {
        complete,
        incomplete: enteredContestants.length - complete,
      };
    },
    [getEnteredContestantsForAgeGroup, isContestantCompleteForSelectedAoc],
  );

  const handleSaveAndNext = useCallback(async () => {
    const canContinuePastMissingScores = await confirmMissingScores();
    if (!canContinuePastMissingScores) return;

    await proceedWithUnsavedChanges(
      () => {
        const nextEnteredIndex = contestants.findIndex(
          (contestant: any, index: number) =>
            index > contestantIndex && isContestantEnteredInAoc(contestant),
        );

        if (nextEnteredIndex >= 0) {
          setContestantIndex(nextEnteredIndex);
          setViewMode("contestant");
            scrollScoringViewToTop();
        } else {
          setViewMode("review");
            scrollScoringViewToTop();
        }
      },
      { autoSave: true },
    );
  }, [
    confirmMissingScores,
    proceedWithUnsavedChanges,
    contestants,
    contestantIndex,
    isContestantEnteredInAoc,
  ]);
  const applyScoringSelection = useCallback(
    (options: {
      aocId: number;
      divisionId?: number | null;
      ageGroup?: any | null;
      forceExactAgeGroup?: boolean;
    }): boolean => {
      const targetAoc = sortedPageantAvailableAOC.find(
        (aoc: any) => Number(aoc?.id ?? 0) === Number(options.aocId),
      );

      if (!targetAoc) return false;

      const candidateDivisions = options.divisionId
        ? sortedEventDivisions.filter(
          (division: any) =>
            getDivisionIdValue(division) === Number(options.divisionId),
        )
        : sortedEventDivisions;

      for (const division of candidateDivisions) {
        const currentDivisionId = getDivisionIdValue(division);
        if (!currentDivisionId) continue;

const effectiveAgeGroupsList: any[] = Array.isArray(effectiveAgeGroups)
  ? Array.from(effectiveAgeGroups as readonly any[]).filter(Boolean)
  : [];

const divisionAgeGroups = effectiveAgeGroupsList.filter((ageGroup: any) => {
          return (
            getContestantsForAgeGroupAndDivision(ageGroup, currentDivisionId)
              .length > 0
          );
        });

        const candidateAgeGroups = options.ageGroup
          ? divisionAgeGroups.filter((ageGroup: any) => {
            const targetId = getAgeGroupIdValue(options.ageGroup);
            const currentId = getAgeGroupIdValue(ageGroup);
            return (
              (targetId && currentId === targetId) ||
              getAgeGroupNameValue(ageGroup) ===
              getAgeGroupNameValue(options.ageGroup)
            );
          })
          : divisionAgeGroups;

        for (const ageGroup of candidateAgeGroups) {
          const ageGroupContestants = getContestantsForAgeGroupAndDivision(
            ageGroup,
            currentDivisionId,
          );

          const enteredContestants = ageGroupContestants.filter(
            (contestant: any) =>
              isContestantEnteredForAocId(contestant, Number(options.aocId)),
          );

          if (!enteredContestants.length && !options.forceExactAgeGroup) {
            continue;
          }

          const firstIncompleteEnteredContestant = enteredContestants.find(
            (contestant: any) =>
              !isContestantCompleteForAocObject(contestant, targetAoc),
          );

          const targetContestantIndex = firstIncompleteEnteredContestant
            ? ageGroupContestants.findIndex(
              (contestant: any) =>
                Number(getContestantIdValue(contestant)) ===
                Number(
                  getContestantIdValue(firstIncompleteEnteredContestant),
                ),
            )
            : enteredContestants.length > 0
              ? ageGroupContestants.findIndex(
                (contestant: any) =>
                  Number(getContestantIdValue(contestant)) ===
                  Number(getContestantIdValue(enteredContestants[0])),
              )
              : 0;

          setSelectedAOC(Number(options.aocId));
          setDivisionId(currentDivisionId);
          setSelectedAgeGroup(ageGroup);
          setContestantIndex(
            targetContestantIndex >= 0 ? targetContestantIndex : 0,
          );
          setViewMode("ageGroupLanding");
          setPickerMode(null);
          setShowContestantPicker(false);
          return true;
        }
      }

      return false;
    },
    [
      sortedPageantAvailableAOC,
      sortedEventDivisions,
      ageGroupsOrdered,
      effectiveAgeGroups,
      getDivisionIdValue,
      getAgeGroupIdValue,
      getAgeGroupNameValue,
      getContestantsForAgeGroupAndDivision,
      isContestantEnteredForAocId,
      isContestantCompleteForAocObject,
      getContestantIdValue,
    ],
  );

  useEffect(() => {
    if (hasAutoSelectedInitialRouteRef.current) return;
   if (
  !sortedPageantAvailableAOC.length ||
  !sortedEventDivisions.length ||
  !effectiveAgeGroups.length
) {
  return;
}

    if (scoringDeepLinkParams.openFilters) {
      hasAutoSelectedInitialRouteRef.current = true;
      setPickerMode("aoc");
      return;
    }

    if (!scoringDeepLinkParams.aocId) return;

const effectiveAgeGroupsList: any[] = Array.isArray(effectiveAgeGroups)
  ? Array.from(effectiveAgeGroups as readonly any[]).filter(Boolean)
  : [];

const targetAgeGroup = scoringDeepLinkParams.ageGroupId
  ? (effectiveAgeGroupsList.find((ageGroup: any) =>
      doesAgeGroupMatchParam(ageGroup, scoringDeepLinkParams.ageGroupId),
    ) ?? null)
  : null;

    const applied = applyScoringSelection({
      aocId: scoringDeepLinkParams.aocId,
      divisionId: scoringDeepLinkParams.divisionId,
      ageGroup: targetAgeGroup,
      forceExactAgeGroup: Boolean(scoringDeepLinkParams.ageGroupId),
    });

    if (applied) {
      hasAutoSelectedInitialRouteRef.current = true;
      userManuallySelectedAgeGroupRef.current = Boolean(
        scoringDeepLinkParams.ageGroupId,
      );
    }
  }, [
    scoringDeepLinkParams,
    sortedPageantAvailableAOC,
    sortedEventDivisions,
    ageGroupsOrdered,
    effectiveAgeGroups,
    doesAgeGroupMatchParam,
    applyScoringSelection,
  ]);

  const firstIncompleteAgeGroup = useMemo(() => {
    if (!availableAgeGroups.length) return null;

    const firstIncompleteWithEntries = availableAgeGroups.find(
      (ageGroup: any) => {
        const counts = getAgeGroupCompletionCounts(ageGroup);
        const total = counts.complete + counts.incomplete;

        return total > 0 && counts.incomplete > 0;
      },
    );

    if (firstIncompleteWithEntries) return firstIncompleteWithEntries;

    return (
      availableAgeGroups.find((ageGroup: any) => {
        const counts = getAgeGroupCompletionCounts(ageGroup);
        return counts.complete + counts.incomplete > 0;
      }) ?? null
    );
  }, [availableAgeGroups, getAgeGroupCompletionCounts]);

  useEffect(() => {
    if (
      !selectedAOC ||
      !divisionId ||
      selectedAgeGroup ||
      !firstIncompleteAgeGroup
    )
      return;

    setSelectedAgeGroup(firstIncompleteAgeGroup);
    setContestantIndex(0);
    setViewMode("ageGroupLanding");
  }, [selectedAOC, divisionId, selectedAgeGroup, firstIncompleteAgeGroup]);

  useEffect(() => {
    if (hasAutoSelectedInitialRouteRef.current) return;
    if (scoringDeepLinkParams.aocId || scoringDeepLinkParams.openFilters)
      return;
    if (
      !sortedPageantAvailableAOC.length ||
      !sortedEventDivisions.length ||
      !ageGroupsOrdered.length
    )
      return;

    for (const aoc of sortedPageantAvailableAOC) {
      const aocId = Number(aoc?.id ?? 0);
      if (!aocId) continue;

      for (const division of sortedEventDivisions) {
        const currentDivisionId = getDivisionIdValue(division);
        if (!currentDivisionId) continue;

        const divisionAgeGroups = ageGroupsOrdered.filter((group: any) => {
          const contestantsInGroup = group?.contestants ?? [];
          return contestantsInGroup.some(
            (contestant: any) =>
              getContestantDivisionIdValue(contestant) === currentDivisionId,
          );
        });

        const ageGroupWithIncompleteEntries = divisionAgeGroups.find(
          (group: any) => {
            const enteredContestants = (group?.contestants ?? []).filter(
              (contestant: any) => {
                return (
                  getContestantDivisionIdValue(contestant) ===
                  currentDivisionId &&
                  isContestantEnteredForAocId(contestant, aocId)
                );
              },
            );

            return (
              enteredContestants.length > 0 &&
              enteredContestants.some(
                (contestant: any) =>
                  !isContestantCompleteForAocObject(contestant, aoc),
              )
            );
          },
        );

        if (ageGroupWithIncompleteEntries) {
          hasAutoSelectedInitialRouteRef.current = true;
          setSelectedAOC(aocId);
          setDivisionId(currentDivisionId);
          setSelectedAgeGroup(ageGroupWithIncompleteEntries);
          setContestantIndex(0);
          setViewMode("ageGroupLanding");
          return;
        }
      }
    }

    const fallbackAoc = sortedPageantAvailableAOC[0];
    const fallbackDivision = sortedEventDivisions[0];
    const fallbackDivisionId = getDivisionIdValue(fallbackDivision);
    const fallbackAgeGroup = ageGroupsOrdered.find((group: any) =>
      (group?.contestants ?? []).some(
        (contestant: any) =>
          getContestantDivisionIdValue(contestant) === fallbackDivisionId,
      ),
    );

    hasAutoSelectedInitialRouteRef.current = true;
    setSelectedAOC(Number(fallbackAoc?.id ?? 0));
    setDivisionId(fallbackDivisionId);
    setSelectedAgeGroup(fallbackAgeGroup ?? null);
    setContestantIndex(0);
    setViewMode("ageGroupLanding");
  }, [
    sortedPageantAvailableAOC,
    sortedEventDivisions,
    ageGroupsOrdered,
    getDivisionIdValue,
    getContestantDivisionIdValue,
    isContestantEnteredForAocId,
    isContestantCompleteForAocObject,
    scoringDeepLinkParams.aocId,
    scoringDeepLinkParams.openFilters,
  ]);

  const totalEnteredContestantsForDivision = useMemo(() => {
    if (!availableAgeGroups.length) return 0;

    const allEntered = availableAgeGroups.flatMap((ageGroup: any) =>
      getEnteredContestantsForAgeGroup(ageGroup),
    );

    const unique = new Map(
      allEntered.map((contestant: any) => [
        Number(contestant?.id ?? contestant?.registeredContestantId ?? 0),
        contestant,
      ]),
    );

    return unique.size;
  }, [availableAgeGroups, getEnteredContestantsForAgeGroup]);

  const isDivisionCompleteForSelectedAoc = useMemo(() => {
    if (!availableAgeGroups.length) return false;

    const allEnteredContestants = availableAgeGroups.flatMap((ageGroup: any) =>
      getEnteredContestantsForAgeGroup(ageGroup),
    );

    if (allEnteredContestants.length === 0) return false;

    const uniqueContestants = Array.from(
      new Map(
        allEnteredContestants.map((contestant: any) => [
          Number(contestant?.id ?? contestant?.registeredContestantId ?? 0),
          contestant,
        ]),
      ).values(),
    );

    return uniqueContestants.every((contestant: any) =>
      isContestantCompleteForSelectedAoc(contestant),
    );
  }, [
    availableAgeGroups,
    getEnteredContestantsForAgeGroup,
    isContestantCompleteForSelectedAoc,
  ]);

  const eligibleContestantAocPairs = useMemo(() => {
    const pairs: Array<{
      contestantId: number;
      divisionId: number | null;
      divisionName: string;
      ageGroupName: string;
      aocId: number;
      aocName: string;
    }> = [];

    for (const contestant of registeredContestants ?? []) {
      const contestantId = Number(
        (contestant as any)?.registeredContestantId ??
        (contestant as any)?.id ??
        0,
      );
      if (!contestantId) continue;

      const divisionIdRaw =
        (contestant as any)?.attributes?.eventDivisionId ??
        (contestant as any)?.contestantAttributes?.eventDivisionId ??
        null;

      const currentDivisionId =
        divisionIdRaw == null || divisionIdRaw === ""
          ? null
          : Number(divisionIdRaw);

      const currentDivisionName =
        (contestant as any)?.attributes?.eventDivisionName ??
        (contestant as any)?.contestantAttributes?.eventDivisionName ??
        "";

      const ageGroupName =
        (contestant as any)?.attributes?.ageGroup ??
        (contestant as any)?.attributes?.ageGroupName ??
        "";

      for (const aoc of pageantAvailableAOC ?? []) {
        const aocId = Number((aoc as any)?.id ?? 0);
        if (!aocId) continue;

        if (isEnteredForAOC(contestant, aocId)) {
          pairs.push({
            contestantId,
            divisionId: currentDivisionId,
            divisionName: currentDivisionName,
            ageGroupName,
            aocId,
            aocName: (aoc as any)?.name ?? "AOC",
          });
        }
      }
    }

    return pairs;
  }, [registeredContestants, pageantAvailableAOC, isEnteredForAOC]);

  const isContestantCompleteForAoc = useCallback(
    (contestantId: number, aocId: number): boolean => {
      const aocObj = (pageantAvailableAOC ?? []).find(
        (aoc: any) => Number(aoc?.id ?? 0) === Number(aocId),
      );
      const categories = aocObj?.aocScoreCategories ?? [];
      if (!categories.length) return false;

      const contestantRows = allFetchedScores?.[Number(contestantId)] ?? [];

      return categories.every((category: any) => {
        const categoryId = Number(category?.id ?? 0);
        const row = contestantRows.find(
          (scoreRow: any) =>
            Number(scoreRow?.aocId ?? scoreRow?.pageantAvailableEventId) ===
            Number(aocId) &&
            Number(scoreRow?.aocScoreCategoryId) === categoryId &&
            Number(scoreRow?.judgeId ?? scoreRow?.eventJudgeId) ===
            Number(judgeId),
        );

        return row?.score !== null && row?.score !== undefined;
      });
    },
    [pageantAvailableAOC, allFetchedScores, judgeId],
  );

  const progressByAoc = useMemo(() => {
    return (pageantAvailableAOC ?? []).map((aoc: any) => {
      const aocId = Number(aoc?.id ?? 0);
      const aocName = aoc?.name ?? "AOC";

      const aocPairs = eligibleContestantAocPairs.filter(
        (pair) => pair.aocId === aocId,
      );

      const completed = aocPairs.reduce((sum, pair) => {
        return (
          sum +
          (isContestantCompleteForAoc(pair.contestantId, pair.aocId) ? 1 : 0)
        );
      }, 0);

      const total = aocPairs.length;
      const percent = total === 0 ? 0 : Math.round((completed / total) * 100);

      return {
        id: aocId,
        name: aocName,
        completed,
        total,
        percent,
      };
    });
  }, [
    pageantAvailableAOC,
    eligibleContestantAocPairs,
    isContestantCompleteForAoc,
  ]);

  const progressByDivision = useMemo(() => {
    return (eventDivisions ?? []).map((division: any) => {
      const currentDivisionId = getDivisionIdValue(division);
      const currentDivisionName = getDivisionNameValue(division);

      const divisionPairs = eligibleContestantAocPairs.filter(
        (pair) => Number(pair.divisionId ?? 0) === currentDivisionId,
      );

      const completed = divisionPairs.reduce((sum, pair) => {
        return (
          sum +
          (isContestantCompleteForAoc(pair.contestantId, pair.aocId) ? 1 : 0)
        );
      }, 0);

      const total = divisionPairs.length;
      const percent = total === 0 ? 0 : Math.round((completed / total) * 100);

      return {
        id: currentDivisionId,
        name: currentDivisionName,
        completed,
        total,
        percent,
      };
    });
  }, [
    eventDivisions,
    getDivisionIdValue,
    getDivisionNameValue,
    eligibleContestantAocPairs,
    isContestantCompleteForAoc,
  ]);

  const selectedAocProgressByDivision = useMemo(() => {
  if (!selectedAOC) return progressByDivision;

  return progressByDivision.map((division) => {
    const divisionPairsForSelectedAoc = eligibleContestantAocPairs.filter(
      (pair) =>
        Number(pair.divisionId ?? 0) === Number(division.id) &&
        Number(pair.aocId) === Number(selectedAOC),
    );

    const total = divisionPairsForSelectedAoc.length;

    const completed = divisionPairsForSelectedAoc.reduce((sum, pair) => {
      return sum + (isContestantCompleteForAoc(pair.contestantId, pair.aocId) ? 1 : 0);
    }, 0);

    const percent = total === 0 ? 0 : Math.round((completed / total) * 100);

    return {
      ...division,
      completed,
      total,
      percent,
    };
  });
}, [
  selectedAOC,
  progressByDivision,
  eligibleContestantAocPairs,
  isContestantCompleteForAoc,
]);

const getDivisionProgressForCurrentMode = useCallback(
  (divisionIdToCheck: number) => {
    const source =
      viewMode === 'sideAwards'
        ? progressByDivision
        : selectedAocProgressByDivision;

    return (
      source.find((division) => Number(division.id) === Number(divisionIdToCheck)) ?? {
        completed: 0,
        total: 0,
        percent: 0,
      }
    );
  },
  [viewMode, progressByDivision, selectedAocProgressByDivision],
);

  const overallEventProgress = useMemo(() => {
    const total = eligibleContestantAocPairs.length;
    const completed = eligibleContestantAocPairs.reduce((sum, pair) => {
      return (
        sum +
        (isContestantCompleteForAoc(pair.contestantId, pair.aocId) ? 1 : 0)
      );
    }, 0);
    const percent = total === 0 ? 0 : Math.round((completed / total) * 100);

    return { completed, total, percent };
  }, [eligibleContestantAocPairs, isContestantCompleteForAoc]);

  const selectedAgeGroupProgress = useMemo(() => {
    if (!selectedAgeGroup) {
      return { completed: 0, total: 0, percent: 0 };
    }

    const enteredContestants =
      getEnteredContestantsForAgeGroup(selectedAgeGroup);
    const total = enteredContestants.length;
    const completed = enteredContestants.filter((contestant: any) =>
      isContestantCompleteForSelectedAoc(contestant),
    ).length;
    const percent = total === 0 ? 0 : Math.round((completed / total) * 100);

    return { completed, total, percent };
  }, [
    selectedAgeGroup,
    getEnteredContestantsForAgeGroup,
    isContestantCompleteForSelectedAoc,
  ]);

  useEffect(() => {
    // Completion prompts intentionally disabled to keep the judge flow clean.
    return;

    if (!selectedAOC || !selectedAocObj || !selectedAgeGroup || !divisionId)
      return;
    if (
      selectedAgeGroupProgress.total === 0 ||
      selectedAgeGroupProgress.percent !== 100
    )
      return;
    if (suppressExactCompletePromptRef.current) return;
    if (hasShownExactCompletePrompt) return;

    setHasShownExactCompletePrompt(true);
    setViewMode("review");
    setCompletionModal({
      show: true,
      type: "aoc",
      title: "YOU HAVE COMPLETED SCORING FOR",
      body: `${selectedAocObj?.name ?? "AOC"} / ${selectedDivisionName || "Division"} / ${getAgeGroupNameValue(selectedAgeGroup) || "Age Group"}`,
    });
  }, [
    selectedAOC,
    selectedAocObj,
    selectedAgeGroup,
    divisionId,
    selectedAgeGroupProgress,
    hasShownExactCompletePrompt,
    selectedDivisionName,
    getAgeGroupNameValue,
  ]);

  useEffect(() => {
    // Event completion prompt intentionally disabled.
    return;

    if (
      overallEventProgress.total === 0 ||
      overallEventProgress.percent !== 100
    )
      return;
    if (promptedEventCompleteRef.current) return;

    promptedEventCompleteRef.current = true;
    setCompletionModal({
      show: true,
      type: "event",
      title: "YOU HAVE COMPLETED SCORING!",
      body: "Every entered contestant in your assigned AOCs has been successfully scored.",
    });
  }, [overallEventProgress]);

  const totalScore = useMemo(() => {
    if (!selectedContestant || !selectedAocObj) return 0;

    return (selectedAocObj.aocScoreCategories ?? []).reduce(
      (sum: number, category: any) => {
        const categoryId = Number(category?.id ?? 0);
        return (
          sum + Number(getCurrentScore(selectedContestant.id, categoryId) ?? 0)
        );
      },
      0,
    );
  }, [selectedContestant, selectedAocObj, getCurrentScore]);

  const totalPointsPossible = useMemo(() => {
    if (!selectedAocObj) return 0;

    return (selectedAocObj.aocScoreCategories ?? []).reduce(
      (sum: number, category: any) => sum + Number(category?.maxScore ?? 0),
      0,
    );
  }, [selectedAocObj]);

  const plusMarkCount = useMemo(() => {
    if (!selectedContestant || !selectedAocObj) return 0;

    return (selectedAocObj.aocScoreCategories ?? []).reduce(
      (sum: number, category: any) => {
        const categoryId = Number(category?.id ?? 0);
        const maxScore = Number(category?.maxScore ?? 0);
        const currentScore = Number(
          getCurrentScore(selectedContestant.id, categoryId) ?? 0,
        );

        const pluses = Math.round(
          (Math.max(0, currentScore - maxScore) + Number.EPSILON) * 10,
        );
        return sum + pluses;
      },
      0,
    );
  }, [selectedContestant, selectedAocObj, getCurrentScore]);

  const isLastContestant = contestantIndex >= contestants.length - 1;

  const currentAgeGroupIndex = availableAgeGroups.findIndex(
    (group: any) => group?.ageGroupId === selectedAgeGroup?.ageGroupId,
  );

  const nextAgeGroup = useMemo(() => {
    if (currentAgeGroupIndex < 0) return null;

    return (
      availableAgeGroups
        .slice(currentAgeGroupIndex + 1)
        .find((ageGroup: any) => {
          const counts = getAgeGroupCompletionCounts(ageGroup);
          return counts.complete + counts.incomplete > 0;
        }) ?? null
    );
  }, [availableAgeGroups, currentAgeGroupIndex, getAgeGroupCompletionCounts]);

  const previousAgeGroup = useMemo(() => {
  if (currentAgeGroupIndex <= 0) return null;

  return (
    [...availableAgeGroups]
      .slice(0, currentAgeGroupIndex)
      .reverse()
      .find((ageGroup: any) => {
        const counts = getAgeGroupCompletionCounts(ageGroup);
        return counts.complete + counts.incomplete > 0;
      }) ?? null
  );
}, [availableAgeGroups, currentAgeGroupIndex, getAgeGroupCompletionCounts]);

const goToAgeGroupLanding = useCallback(
  (ageGroup: any) => {
    if (!ageGroup || !divisionId) return;

    setSelectedAgeGroup(ageGroup);
    setContestantIndex(0);
    setViewMode("ageGroupLanding");
    setPickerMode(null);
    setShowContestantPicker(false);
  },
  [divisionId],
);

  useEffect(() => {
    if (suppressExactCompletePromptRef.current) {
      setHasShownExactCompletePrompt(
        selectedAgeGroupProgress.total > 0 && selectedAgeGroupProgress.percent === 100,
      );
      suppressExactCompletePromptRef.current = false;
      return;
    }

    setHasShownExactCompletePrompt(false);
  }, [
    selectedAOC,
    divisionId,
    selectedAgeGroup,
    selectedAgeGroupProgress.total,
    selectedAgeGroupProgress.percent,
  ]);

  const getContestantShortName = useCallback((contestant: any): string => {
    const firstName = String(
      contestant?.firstName ??
      contestant?.contestantName?.split(" ")?.[0] ??
      "",
    ).trim();

    const lastName = String(
      contestant?.lastName ??
      contestant?.contestantName?.split(" ")?.slice(1).join(" ") ??
      "",
    ).trim();

    const lastInitial = lastName ? `${lastName.charAt(0).toUpperCase()}.` : "";
    return (
      `${firstName} ${lastInitial}`.trim() ||
      contestant?.fullName ||
      "Contestant"
    );
  }, []);

  const selectedContestantDisplayName = selectedContestant
    ? getContestantShortName(selectedContestant)
    : "Contestant";


  const beginScoringForAgeGroup = useCallback(() => {
    if (!contestants.length) return;

    const firstIncompleteEnteredIndex = contestants.findIndex(
      (contestant: any) =>
        isContestantEnteredInAoc(contestant) &&
        !isContestantCompleteForSelectedAoc(contestant),
    );

    const firstEnteredIndex = contestants.findIndex((contestant: any) =>
      isContestantEnteredInAoc(contestant),
    );

    const nextIndex =
      firstIncompleteEnteredIndex >= 0
        ? firstIncompleteEnteredIndex
        : firstEnteredIndex >= 0
          ? firstEnteredIndex
          : 0;

    setContestantIndex(nextIndex);
    setShowContestantPicker(false);
    setViewMode("contestant");
  }, [contestants, isContestantEnteredInAoc, isContestantCompleteForSelectedAoc]);

  const getContestantAocStatus = useCallback(
    (contestant: any): ContestantAocStatus => {
      const isEntered = isContestantEnteredInAoc(contestant);
      const isComplete = isContestantCompleteForSelectedAoc(contestant);

      if (isEntered) {
        return isComplete
          ? { isEntered, isComplete, label: "COMPLETE", badgeClass: "bg-success" }
          : { isEntered, isComplete, label: "INCOMPLETE", badgeClass: "bg-warning" };
      }

      return isComplete
        ? { isEntered, isComplete, label: "SCORED", badgeClass: "bg-warning" }
        : { isEntered, isComplete, label: "NOT ENTERED", badgeClass: "bg-muted" };
    },
    [isContestantEnteredInAoc, isContestantCompleteForSelectedAoc],
  );

  const renderContestantStatusBadge = useCallback(
    (contestant: any) => {
      const status = getContestantAocStatus(contestant);

      return (
        <span className={`badge ${status.badgeClass} mx-2`}>
          {status.label}
        </span>
      );
    },
    [getContestantAocStatus],
  );

  const toggleScoreTableSort = useCallback((field: "number" | "rank") => {
    setScoreTableSort((prev) => {
      if (prev.field === field) {
        return {
          field,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        field,
        direction: "asc",
      };
    });
  }, []);

  const renderAocSelectionButton = useCallback(
    (
      aoc: any,
      isSelected: boolean,
      onClick: () => void,
      selectedVariant: string,
      unselectedVariant: string,
    ) => {
      return (
        <Button
          key={aoc.id}
          variant={isSelected ? selectedVariant : unselectedVariant}
          className="w-100 text-start p-3 h-100"
          onClick={onClick}
        >
          <div className="fw-bold">{aoc.name}</div>
        </Button>
      );
    },
    [],
  );

  const renderDivisionSelectionButton = useCallback(
    (
      division: any,
      isSelected: boolean,
      onClick: () => void,
      selectedVariant: string,
      unselectedVariant: string,
    ) => {
      const currentDivisionId = getDivisionIdValue(division);
      const currentDivisionName = getDivisionNameValue(division);

      return (
        <Button
          key={currentDivisionId}
          variant={isSelected ? selectedVariant : unselectedVariant}
          className="w-100 text-start p-3 h-100"
          onClick={onClick}
        >
          <div className="fw-bold">{currentDivisionName}</div>
        </Button>
      );
    },
    [getDivisionIdValue, getDivisionNameValue],
  );

const reviewTable = (
  <Card className="shadow-sm border-0">
    <Card.Body className="p-3">
      {(() => {
        const categories = selectedAocObj?.aocScoreCategories ?? [];
        const isPhotoEvent =
          getFeedbackVariantForAoc(selectedAocObj) === "photo";

        const getContestantReviewNotes = (contestantId: number): ScoreFeedbackDraft => {
          const feedbackKey =
            selectedAOC && judgeId
              ? makeFeedbackKey(Number(contestantId), Number(selectedAOC), Number(judgeId))
              : "";

          return (
            feedbackDrafts[feedbackKey] ??
            parseScoreNotesString(getFetchedNotes(Number(contestantId)))
          );
        };

        const hasReviewNote = (draft: ScoreFeedbackDraft): boolean => {
          return Boolean(
            draft.customNote?.trim() ||
              draft.excelledIn?.length ||
              draft.improveOn?.length ||
              draft.deductionApplied,
          );
        };

        const rowsWithTotals = contestants.map((contestant: any) => {
          const categoryScores = categories.map((cat: any) => {
            const categoryId = Number(cat?.id ?? 0);
            return getFetchedScore(contestant.id, categoryId);
          });

          const total = categoryScores.reduce(
            (sum: number, score: number | null) => sum + Number(score ?? 0),
            0,
          );

          const pluses = categories.reduce((sum: number, cat: any) => {
            const categoryId = Number(cat?.id ?? 0);
            const score = Number(getFetchedScore(contestant.id, categoryId) ?? 0);
            const maxScore = Number(cat?.maxScore ?? 0);

            return (
              sum +
              Math.round(
                (Math.max(0, score - maxScore) + Number.EPSILON) * 10,
              )
            );
          }, 0);

          const notesDraft = getContestantReviewNotes(Number(contestant.id));

          return {
            contestant,
            categoryScores,
            total,
            pluses,
            notesDraft,
            hasNote: hasReviewNote(notesDraft),
            selectedPhoto: notesDraft.selectedPhoto ?? null,
            isComplete: isContestantComplete(contestant.id),
          };
        });

        const rankMap = computeRanksWithTies(
          rowsWithTotals.map((row) => ({
            id: row.contestant.id,
            score: row.total,
          })),
        );

        const sortedRows = [...rowsWithTotals].sort((a, b) => {
          if (scoreTableSort.field === "rank") {
            const rankA =
              rankMap.get(a.contestant.id)?.rank ?? Number.MAX_SAFE_INTEGER;
            const rankB =
              rankMap.get(b.contestant.id)?.rank ?? Number.MAX_SAFE_INTEGER;

            if (rankA !== rankB) {
              return scoreTableSort.direction === "asc"
                ? rankA - rankB
                : rankB - rankA;
            }

            const contestantDelta =
              contestantNumberAsNum(a.contestant) -
              contestantNumberAsNum(b.contestant);

            return scoreTableSort.direction === "asc"
              ? contestantDelta
              : -contestantDelta;
          }

          const contestantDelta =
            contestantNumberAsNum(a.contestant) -
            contestantNumberAsNum(b.contestant);

          if (contestantDelta !== 0) {
            return scoreTableSort.direction === "asc"
              ? contestantDelta
              : -contestantDelta;
          }

          const rankA =
            rankMap.get(a.contestant.id)?.rank ?? Number.MAX_SAFE_INTEGER;
          const rankB =
            rankMap.get(b.contestant.id)?.rank ?? Number.MAX_SAFE_INTEGER;

          return scoreTableSort.direction === "asc"
            ? rankA - rankB
            : rankB - rankA;
        });

        const renderTotalCell = (total: number, pluses: number, hasNote: boolean) => (
          <span className="d-inline-flex align-items-center gap-1">
            <strong>{formatTrimmedScore(total)}</strong>

            {pluses > 0 && (
              <em className="fw-bold">
                {"+".repeat(Math.min(pluses, 10))}
                {pluses > 10 ? `+${pluses - 10}` : ""}
              </em>
            )}

            {hasNote && (
              <CsLineIcons
                icon="note"
                size="14"
                className="text-primary"
              />
            )}
          </span>
        );

        return (
          <>
            <div className="d-md-none">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0 judge-mobile-review-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Total</th>
                      {isPhotoEvent && <th>Photo</th>}
                      <th>Status</th>
                      <th>Rank</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map(
                      ({
                        contestant,
                        total,
                        pluses,
                        hasNote,
                        selectedPhoto,
                        isComplete,
                      }, index) => {
                        const originalContestantIndex = contestants.findIndex(
                          (c: any) => Number(c.id) === Number(contestant.id),
                        );

                        const handleOpenContestant = () => {
                          if (originalContestantIndex >= 0) {
                            void proceedWithUnsavedChanges(() => {
                              setContestantIndex(originalContestantIndex);
                              setViewMode("contestant");
                            });
                          }
                        };

                        const isEntered = isContestantEnteredInAoc(contestant);
                        const rankInfo = rankMap.get(contestant.id) ?? {
                          rank: Number.NaN,
                          isTie: false,
                        };

                        return (
                          <tr
                            key={contestant.id}
                            role="button"
                            onClick={handleOpenContestant}
                            className={!isEntered ? "bg-light" : ""}
                            style={{ cursor: "pointer" }}
                          >
                            <td className="fw-bold">
                              {contestant.contestantNumber || index + 1}
                            </td>
                            <td className="text-truncate" style={{ maxWidth: 120 }}>
                              {getContestantShortName(contestant)}
                            </td>
                            <td>{renderTotalCell(total, pluses, hasNote)}</td>
                            {isPhotoEvent && <td>{selectedPhoto ?? "—"}</td>}
                            <td>
                              {!isEntered ? (
                                <span className="badge bg-muted">NOT ENTERED</span>
                              ) : isComplete ? (
                                <span className="badge bg-success">DONE</span>
                              ) : (
                                <span className="badge bg-warning">OPEN</span>
                              )}
                            </td>
                            <td>
                              {Number.isFinite(rankInfo.rank)
                                ? `${rankInfo.rank}${rankInfo.isTie ? "T" : ""}`
                                : ""}
                            </td>
                          </tr>
                        );
                      },
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="table-responsive d-none d-md-block">
              <table className="table align-middle">
                <thead className="text-medium">
                  <tr>
                    <th role="button" onClick={() => toggleScoreTableSort("number")}>
                      #
                    </th>
                    <th>Name</th>

                    {categories.map((cat: any) => (
                      <th key={cat.id}>{cat.scoringCategoryName}</th>
                    ))}

                    <th>Total</th>
                    {isPhotoEvent && <th>Selected Photo</th>}
                    <th>Status</th>
                    <th role="button" onClick={() => toggleScoreTableSort("rank")}>
                      Rank
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {sortedRows.map(
                    (
                      {
                        contestant,
                        categoryScores,
                        total,
                        pluses,
                        hasNote,
                        selectedPhoto,
                      },
                      index,
                    ) => {
                      const contestantDisplayName =
                        getContestantShortName(contestant);

                      const rankInfo = rankMap.get(contestant.id) ?? {
                        rank: Number.NaN,
                        isTie: false,
                      };

                      const originalContestantIndex = contestants.findIndex(
                        (c: any) => Number(c.id) === Number(contestant.id),
                      );

                      const handleOpenContestant = () => {
                        if (originalContestantIndex >= 0) {
                          void proceedWithUnsavedChanges(() => {
                            setContestantIndex(originalContestantIndex);
                            setViewMode("contestant");
                          });
                        }
                      };

                      const isEntered = isContestantEnteredInAoc(contestant);

                      return (
                        <tr
                          key={contestant.id}
                          role="button"
                          onClick={handleOpenContestant}
                          className={!isEntered ? "bg-light" : ""}
                          style={{ cursor: "pointer" }}
                        >
                          <td>{contestant.contestantNumber || index + 1}</td>
                          <td>{contestantDisplayName}</td>

                          {categoryScores.map(
                            (score: number | null, scoreIndex: number) => (
                              <td key={`${contestant.id}-${scoreIndex}`}>
                                {score !== null ? formatScoreButtonLabel(score) : ""}
                              </td>
                            ),
                          )}

                          <td>{renderTotalCell(total, pluses, hasNote)}</td>

                          {isPhotoEvent && <td>{selectedPhoto ?? "—"}</td>}

                          <td>{renderContestantStatusBadge(contestant)}</td>

                          <td>
                            {Number.isFinite(rankInfo.rank)
                              ? `${rankInfo.rank}${rankInfo.isTie ? " (TIE)" : ""}`
                              : ""}
                          </td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </table>
            </div>
          </>
        );
      })()}
    </Card.Body>
  </Card>
);

  const savingContestantName = selectedContestant
    ? getContestantShortName(selectedContestant)
    : "Contestant";

  const savingContestantNumber = selectedContestant?.contestantNumber || "";
  const savingAgeGroupName = selectedAgeGroup?.ageGroupName || "";
  const savingAocName = selectedAocObj?.name || "";

  const currentFeedbackKey =
    selectedContestant && selectedAOC && judgeId
      ? makeFeedbackKey(
        Number(selectedContestant.id),
        Number(selectedAOC),
        Number(judgeId),
      )
      : null;

  const currentNoteString = currentFeedbackKey
    ? buildScoreNotesString(feedbackDrafts[currentFeedbackKey])
    : "";

  // Keep Bootstrap modals animation-free to avoid React DOM removeChild errors during rapid mount/unmount.
  const savingModal = (
    <Modal
      show={isSaving}
      centered
      backdrop="static"
      keyboard={false}
      animation={false}
      className="z-index-1050"
    >
      <Modal.Body className="p-4 text-center">
        <div className="mb-3">
          <Spinner animation="border" />
        </div>

        <div className="text-muted small mb-2">Saving Scores</div>

        <h3 className="mb-2">
          #{savingContestantNumber} {savingContestantName}
        </h3>

        <div className="text-muted mb-1">{savingAgeGroupName}</div>
        <div className="text-muted mb-3">{savingAocName}</div>

        <div className="display-4 fw-bold mb-2">
          {formatTrimmedScore(totalScore)} /{" "}
          {formatTrimmedScore(totalPointsPossible)}
        </div>

        <span>
          <em>
            {plusMarkCount > 0
              ? ` (${"+".repeat(Math.min(plusMarkCount, 10))}${plusMarkCount > 10 ? `+${plusMarkCount - 10}` : ""
              })`
              : ""}
          </em>
        </span>

        {currentNoteString && (
          <div className="mt-3 text-start">
            <div className="text-muted small mb-1">Judge Note</div>
            <div className="border rounded p-2 bg-light small text-body">
              {currentNoteString}
            </div>
          </div>
        )}

        <div className="text-muted mt-3">
          Please wait while scores are being saved.
        </div>
      </Modal.Body>
    </Modal>
  );

  const confirmUiModal = (
    <Modal
      show={confirmModal.show}
      onHide={() => closeConfirmModal(false)}
      centered
      animation={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>{confirmModal.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{confirmModal.body}</Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-secondary"
          onClick={() => closeConfirmModal(false)}
        >
          {confirmModal.cancelText}
        </Button>
        <Button
          variant={confirmModal.confirmVariant ?? "primary"}
          onClick={() => closeConfirmModal(true)}
        >
          {confirmModal.confirmText}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const missingScoreUiModal = (
<Modal
  show={missingScoreModal.show}
  onHide={() => closeMissingScoreModal(false)}
  centered
>
  <Modal.Header closeButton>
    <Modal.Title>Missing Scores</Modal.Title>
  </Modal.Header>

  <Modal.Body>
    <p className="mb-3">
      This contestant has some scoring categories missing.
    </p>

    <div className="border rounded bg-light p-3 text-body">
      <div className="fw-semibold mb-2">
        Missing Categories:
      </div>

      <ul className="mb-0 ps-3">
        {missingScoreModal.categories.map((category) => (
          <li key={category.id}>
            {category.name}
          </li>
        ))}
      </ul>
    </div>
  </Modal.Body>

  <Modal.Footer>
        <Button
      variant="outline-danger"
      onClick={() => closeMissingScoreModal(true)}
    >
      Continue Without Scores
    </Button>

    <Button
      variant="primary"
      className="btn-large"
      onClick={() => closeMissingScoreModal(false)}
    >
      Go Back to Finish Scoring
    </Button>


  </Modal.Footer>
</Modal>
  );

  const completionUiModal = (
    <Modal
      show={completionModal.show}
      onHide={() => setCompletionModal((prev) => ({ ...prev, show: false }))}
      centered
      animation={false}
    >
      <Modal.Body className="p-4 text-center">
        <div className="text-success mb-3">
          <CsLineIcons icon="check-circle" size="52" />
        </div>
        <h2 className="mb-2">{completionModal.title}</h2>
        <div className="fw-bold fs-4 text-body mb-3">
          {completionModal.body}
        </div>
        <p className="text-muted mb-4">
          You can review the scores or make changes from this screen.
        </p>
        <div className="d-grid gap-2">
          <Button
            variant="primary"
            onClick={() => {
              setCompletionModal((prev) => ({ ...prev, show: false }));
              setViewMode("review");
            }}
          >
            Review / Make Changes
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() =>
              setCompletionModal((prev) => ({ ...prev, show: false }))
            }
          >
            Close
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );

const divisionCompleteUiModal = (
  <Modal
    show={divisionCompleteModal}
    onHide={() => setDivisionCompleteModal(false)}
    centered
    animation={false}
  >
    <Modal.Body className="p-4 text-center">
      <div className="text-success mb-3">
        <CsLineIcons icon="check-circle" size="52" />
      </div>

      <h2 className="mb-2">YOU ARE COMPLETE WITH</h2>

      <div className="fw-bold fs-4 text-body mb-3">
        {selectedAocObj?.name ?? "AOC"} in{" "}
        {selectedDivisionName || "Division"}
      </div>

      <div className="d-grid gap-2">
        <Button variant="success" onClick={() => history.push(judgeHomePath)}>
          Go Back Home
        </Button>

        <Button
          variant="primary"
          onClick={() => {
            setDivisionCompleteModal(false);
            setPickerMode("aoc");
          }}
        >
          Score Next Event
        </Button>

        <Button
          variant="outline-primary"
          onClick={() => {
            setDivisionCompleteModal(false);
            setPickerMode("division");
          }}
        >
          Score Different Division
        </Button>

        <Button
          variant="outline-secondary"
          onClick={() => setDivisionCompleteModal(false)}
        >
          Stay Here
        </Button>
      </div>
    </Modal.Body>
  </Modal>
);

  const selectedAocProgress = progressByAoc.find(
    (progress) => Number(progress.id) === Number(selectedAOC),
  ) ?? {
    completed: 0,
    total: 0,
    percent: 0,
  };

  const selectedDivisionProgress = progressByDivision.find(
    (progress) => Number(progress.id) === Number(divisionId),
  ) ?? {
    completed: 0,
    total: 0,
    percent: 0,
  };

  const formatProgressLine = (progress: {
    completed: number;
    total: number;
    percent: number;
  }) => (progress.total > 0 ? `${progress.percent}% complete` : "No entries");

 

  const scoringFilters = (
    <div className="row g-2">
    <div className={`col-12 col-md-${hasRealAgeGroups ? "4" : "6"}`}>
        <Button
          variant="foreground"
          className={`w-100 h-100 text-start p-2 border-0 position-relative`}
          onClick={() => togglePickerMode("aoc")}
        >
          <span
            className="position-absolute top-0 end-0 m-1 text-muted"
            style={{ opacity: 0.6 }}
          >
            <CsLineIcons icon="edit" size="14" />
          </span>
          <div className="judge-filter-detail small text-muted">
            Area of Competition
          </div>
          <div className="cta-1 text-body fw-bold text-truncate">
            {viewMode === "sideAwards" ? "SIDE AWARDS" : selectedAocObj?.name ?? "Select AOC"}
          </div>
        </Button>
      </div>

  <div className={`col-12 col-md-${hasRealAgeGroups ? "4" : "6"}`}>
        <Button
          variant="foreground"
          className={`w-100 h-100 text-start p-2 border-0 position-relative `}
          onClick={() => togglePickerMode("division")}
        >
          <span
            className="position-absolute top-0 end-0 m-1 text-muted"
            style={{ opacity: 0.6 }}
          >
            <CsLineIcons icon="edit" size="14" />
          </span>
          <div className="judge-filter-detail small text-muted">Division</div>
          <div className="cta-1 text-body fw-bold text-truncate">
            {selectedDivisionName || "Select Division"}
          </div>
        </Button>
      </div>

 {hasRealAgeGroups && (
  <div className="col-6 col-md-4">
    <Button
      variant="foreground"
      className="w-100 h-100 text-start p-2 border-0 position-relative"
      onClick={() => togglePickerMode("ageGroup")}
    >
      <span
        className="position-absolute top-0 end-0 m-1 text-muted"
        style={{ opacity: 0.6 }}
      >
        <CsLineIcons icon="edit" size="14" />
      </span>

      <div className="judge-filter-detail small text-muted">
        Age Group
      </div>

      <div className="cta-1 text-body fw-bold text-truncate">
        {selectedAgeGroup?.ageGroupName ?? "Select Age Group"}
      </div>
    </Button>
  </div>
)}
    </div>
  );

  const filterSelectionDropdown = null;

  const getPickerModalTitle = (): string => {
    if (pickerMode === "aoc") return "Change Area of Competition";
    if (pickerMode === "division") return "Change Division";
    if (pickerMode === "ageGroup") return "Change Age Group";
    return "Change Filter";
  };

  const pickerModal = (
    <Modal
      show={pickerMode !== null}
      onHide={() => setPickerMode(null)}
      centered
      size="lg"
      animation={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>{getPickerModalTitle()}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {pickerMode === "aoc" && (
          <div
            className="row g-2 row-cols-1 row-cols-md-2 row-cols-xl-3"
            style={{ maxHeight: "70vh", overflowY: "auto" }}
          >
            {sortedPageantAvailableAOC.map((aoc: any) => {
              const isSelected = Number(selectedAOC) === Number(aoc.id);
              const progress = progressByAoc.find(
                (p: any) => Number(p.id) === Number(aoc.id),
              );
              const percent = progress?.percent ?? 0;
              const hasEntries = (progress?.total ?? 0) > 0;

              return (
                <div key={aoc.id} className="col">
                  <Button
                    variant={isSelected ? "primary" : "outline-body"}
                    className="text-start p-3 w-100 h-100 position-relative"
                    onClick={() => {
                      void proceedWithUnsavedChanges(() => {
                        setSelectedAOC(Number(aoc.id));
                        setSelectedAgeGroup(null);
                        setContestantIndex(0);
                        setViewMode("ageGroupLanding");
                        setPickerMode(null);
                      });
                    }}
                  >
                    <span className="position-absolute top-0 end-0 m-2 small fw-bold">
                      {!hasEntries ? (
                        <span className="badge bg-secondary">NO ENTRIES</span>
                      ) : (
                                               <span
      className={`text-medium badge ${
        percent === 100 ? "bg-success" : "bg-warning"
      }`}
    >
                          {percent}%
                        </span>
                      )}
                    </span>

                    <div className="fw-bold text-truncate pe-5">
                      {aoc.name}
                    </div>
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {pickerMode === "division" && (
          <div
            className="row g-2 row-cols-1 row-cols-md-2 row-cols-xl-3"
            style={{ maxHeight: "70vh", overflowY: "auto" }}
          >
        {sortedEventDivisions.map((division: any) => {
  const currentDivisionId = getDivisionIdValue(division);
  const currentDivisionName = getDivisionNameValue(division);
  const isSelected = Number(divisionId) === currentDivisionId;

  const divisionProgress = getDivisionProgressForCurrentMode(currentDivisionId);

  const percent = divisionProgress.percent ?? 0;
  const hasEntries = (divisionProgress.total ?? 0) > 0;

  return (
    <div key={currentDivisionId} className="col">
      <Button
        variant={isSelected ? "primary" : "outline-body"}
        className="text-start p-3 w-100 h-100 position-relative"
        onClick={() => {
          void proceedWithUnsavedChanges(() => {
            setDivisionId(currentDivisionId);
            setSelectedAgeGroup(null);
            setContestantIndex(0);
            setViewMode("ageGroupLanding");
            setPickerMode(null);
          });
        }}
      >
        <span className="position-absolute top-0 end-0 m-2 small fw-bold">
          {!hasEntries ? (
            <span className="badge bg-secondary">NO ENTRIES</span>
          ) : (
            <span
              className={`text-medium badge ${
                percent === 100 ? "bg-success" : "bg-warning"
              }`}
            >
              {percent}%
            </span>
          )}
        </span>

        <div className="fw-bold text-truncate pe-5">
          {currentDivisionName}
        </div>

        <div className={isSelected ? "small text-white-50 mt-1" : "small text-muted mt-1"}>
          {divisionProgress.completed}/{divisionProgress.total} complete
        </div>
      </Button>
    </div>
  );
})}
          </div>
        )}

        {pickerMode === "ageGroup" && (
          <>
            {!divisionId ? (
              <div className="border rounded bg-light p-4 text-center text-muted">
                Select a division before choosing an age group.
              </div>
            ) : availableAgeGroups.length === 0 ? (
              <div className="border rounded bg-light p-4 text-center text-muted">
                No age groups found for this division.
              </div>
            ) : (
              <div
                className="row g-2 row-cols-2 row-cols-md-3"
                style={{ maxHeight: "70vh", overflowY: "auto" }}
              >
                {availableAgeGroups.map((ag: any) => {
                  const isSelected =
                    getAgeGroupIdValue(selectedAgeGroup) ===
                    getAgeGroupIdValue(ag);

                  const counts = getAgeGroupCompletionCounts(ag);
                  const total = counts.complete + counts.incomplete;
                  const percent =
                    total === 0
                      ? 0
                      : Math.round((counts.complete / total) * 100);

                  return (
                    <div
                      key={getAgeGroupIdValue(ag) || getAgeGroupNameValue(ag)}
                      className="col"
                    >
                      <Button
                        variant={isSelected ? "primary" : "outline-body"}
                        className="text-start p-3 w-100 h-100 position-relative"
                        onClick={() => {
                          void proceedWithUnsavedChanges(() => {
                            userManuallySelectedAgeGroupRef.current = true;
                            setSelectedAgeGroup(ag);
                            setContestantIndex(0);
                            setViewMode("ageGroupLanding");
                            setPickerMode(null);
                          });
                        }}
                      >
                   <span className="position-absolute top-0 end-0 m-2 small fw-bold">
  {total === 0 ? (
    <span className="badge bg-muted">NO ENTRIES</span>
  ) : (
    <span
      className={`text-medium badge ${
        counts.complete === total ? "bg-success" : "bg-warning"
      }`}
    >
      {counts.complete}/{total}
    </span>
  )}
</span>

                        <div className="fw-bold text-truncate pe-5">
                          {getAgeGroupNameValue(ag)}
                        </div>
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => setPickerMode(null)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const getSideAwardWinnerValue = (award: any) => {
    return (
      award?.winner ??
      award?.winnerContestant ??
      award?.selectedContestant ??
      award?.registeredPageantContestant ??
      award?.registeredContestant ??
      award?.contestant ??
      award?.winnerId ??
      award?.winnerContestantId ??
      award?.registeredPageantContestantId ??
      award?.registeredContestantId ??
      null
    );
  };

  const getSideAwardAgeGroupId = (award: any): number => {
    return Number(
      award?.ageGroupId ??
      award?.pageantAgeGroupId ??
      award?.awardAgeGroupId ??
      award?.ageGroup?.id ??
      award?.ageGroup?.ageGroupId ??
      award?.winner?.ageGroupId ??
      award?.winnerContestant?.ageGroupId ??
      award?.registeredPageantContestant?.ageGroupId ??
      award?.registeredContestant?.ageGroupId ??
      0,
    );
  };

  const getSideAwardDivisionId = (award: any): number => {
    return Number(
      award?.divisionId ??
      award?.eventDivisionId ??
      award?.awardDivisionId ??
      award?.division?.id ??
      award?.division?.divisionId ??
      award?.winner?.divisionId ??
      award?.winner?.attributes?.eventDivisionId ??
      award?.winnerContestant?.divisionId ??
      award?.winnerContestant?.attributes?.eventDivisionId ??
      award?.registeredPageantContestant?.divisionId ??
      award?.registeredPageantContestant?.attributes?.eventDivisionId ??
      award?.registeredContestant?.divisionId ??
      award?.registeredContestant?.attributes?.eventDivisionId ??
      0,
    );
  };

  const selectedAgeGroupId = selectedAgeGroup
    ? getAgeGroupIdValue(selectedAgeGroup)
    : 0;

  const sideAwardsForSelectedAgeGroup = useMemo(() => {
    return (pageantAvailableAwards ?? []).filter((award: any) => {
      const awardAgeGroupId = getSideAwardAgeGroupId(award);
      const awardDivisionId = getSideAwardDivisionId(award);

      const matchesAgeGroup =
        !selectedAgeGroupId || !awardAgeGroupId
          ? true
          : awardAgeGroupId === selectedAgeGroupId;

      const matchesDivision =
        !divisionId || !awardDivisionId
          ? true
          : awardDivisionId === Number(divisionId);

      return matchesAgeGroup && matchesDivision;
    });
  }, [pageantAvailableAwards, selectedAgeGroupId, divisionId]);

  const sideAwardContestants = useMemo(() => {
    const sorted = [...contestants].sort((a: any, b: any) => {
      const aNum = contestantNumberAsNum(a);
      const bNum = contestantNumberAsNum(b);
      return sortDesc ? bNum - aNum : aNum - bNum;
    });

    // console.log("[SideAwards Parent] sorted contestants", {
    //   sortDesc,
    //   order: sorted.map((c: any) => ({
    //     id: getContestantIdValue(c),
    //     number: c?.contestantNumber,
    //     name: c?.fullName ?? c?.contestantName,
    //   })),
    // });

    return sorted;
  }, [contestants, sortDesc, contestantNumberAsNum, getContestantIdValue]);

  const getSideAwardWinnerId = useCallback((award: any): number => {
    return Number(
      award?.winner?.id ??
      award?.winner?.registeredContestantId ??
      award?.winner?.registeredPageantContestantId ??
      award?.winnerContestant?.id ??
      award?.winnerContestant?.registeredContestantId ??
      award?.winnerContestant?.registeredPageantContestantId ??
      award?.selectedContestant?.id ??
      award?.selectedContestant?.registeredContestantId ??
      award?.selectedContestant?.registeredPageantContestantId ??
      award?.registeredPageantContestant?.id ??
      award?.registeredContestant?.id ??
      award?.contestant?.id ??
      award?.winnerId ??
      award?.winnerContestantId ??
      award?.registeredPageantContestantId ??
      award?.registeredContestantId ??
      0,
    );
  }, []);

  const currentAgeGroupContestantIds = useMemo(() => {
    return new Set(contestants.map((contestant: any) => getContestantIdValue(contestant)));
  }, [contestants, getContestantIdValue]);





  const scoringHeader = (
    <div className="judge-current-contestant-header mt-2 w-100">
      {viewMode === "review" ? (
        <div className="bg-foreground p-2">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div
                className={`fw-bold fs-2 ${selectedAgeGroupProgress.percent === 100 ? "text-success" : "text-muted"
                  }`}
              >
                {selectedAgeGroupProgress.percent}%
              </div>
              <div className="small text-muted">
                Complete ({selectedAgeGroupProgress.completed}/{selectedAgeGroupProgress.total})
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small d-none d-sm-inline">Sort:</span>

              <Button
                size="sm"
                variant={scoreTableSort.field === "number" ? "primary" : "outline-secondary"}
                onClick={() => toggleScoreTableSort("number")}
              >
                #
              </Button>

              <Button
                size="sm"
                variant={scoreTableSort.field === "rank" ? "primary" : "outline-secondary"}
                onClick={() => toggleScoreTableSort("rank")}
              >
                Rank
              </Button>
            </div>
          </div>
        </div>
      ) : viewMode === "sideAwards" ? (
        <div className="bg-foreground rounded p-3">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div className="min-width-0">
              <div className="text-truncate text-body">
                <span className="fs-2 cta-1 fw-bold">{sideAwardHeaderSummary.total}</span>
                  <span>Award{sideAwardHeaderSummary.total === 1 ? "" : "s"} </span>
                  / <span className="fs-2 cta-1 fw-bold">{sideAwardHeaderSummary.completed}</span>
                  <span>   Winner{sideAwardHeaderSummary.completed === 1 ? "" : "s"}</span>

                <span>
                       <div
                  className={`mx-2 badge ${sideAwardHeaderSummary.total > 0 &&
                      sideAwardHeaderSummary.incomplete === 0
                      ? "bg-success"
                      : "bg-warning"
                    }`}
                >
                  {sideAwardHeaderSummary.total > 0 &&
                    sideAwardHeaderSummary.incomplete === 0
                    ? "COMPLETE"
                    : "INCOMPLETE"}
                </div>
                </span>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3 flex-wrap">
              <Form.Check
                type="switch"
                id="side-award-sort-switch"
                label={sortDesc ? "High → Low" : "Low → High"}
                checked={sortDesc}
                onChange={(e) => setSortDesc(e.currentTarget.checked)}
              />
            </div>
          </div>
        </div>

      ) : viewMode === "contestant" && (!selectedContestant || !selectedAocObj) ? (
        <div className="bg-foreground rounded p-3">
          <div className="d-flex justify-content-between align-items-center gap-3">
            <div className="min-width-0">
              <div className="judge-filter-detail small text-muted">Age Group</div>
              <div className="fs-2 cta-1 fw-bold text-body text-truncate">
                {getAgeGroupName(selectedAgeGroup) || "Age Group"}
              </div>
              <div className="small text-muted">
                {contestants.length} contestant{contestants.length === 1 ? "" : "s"}
                {selectedAocObj?.name ? ` for ${selectedAocObj.name}` : ""}
              </div>
            </div>

            <div className="text-end flex-shrink-0">
              <div
                className={`fw-bold fs-2 ${selectedAgeGroupProgress.percent === 100 ? "text-success" : "text-muted"
                  }`}
              >
                {selectedAgeGroupProgress.percent}%
              </div>
              <div className="small text-muted">
                Complete ({selectedAgeGroupProgress.completed}/{selectedAgeGroupProgress.total})
              </div>
            </div>
          </div>
        </div>
      ) : viewMode === "contestant" && selectedContestant && selectedAocObj ? (
        <div className="bg-foreground rounded p-2 judge-condensed-contestant-card">
          <div className="d-flex justify-content-between align-items-start gap-2">
            <div className="min-width-0">
              <Button
                type="button"
                variant="foreground"
                className="p-0 text-decoration-none text-start w-100 judge-contestant-picker-trigger"
                onClick={() => {
                  void proceedWithUnsavedChanges(() => {
                    setPickerMode(null);
                    setShowContestantPicker(false);
                    setViewMode("ageGroupLanding");
                  });
                }}
              >
                <div className="judge-filter-detail small text-muted">Contestant</div>

                <div className="d-flex">
                  <span className="fs-2 fw-bold lh-1 text-truncate">
                    #{selectedContestant.contestantNumber || "-"} {selectedContestantDisplayName}
                  </span>

                  <span> {renderContestantStatusBadge(selectedContestant)}</span>

                  <span className="top-0 end-0 m-1 text-muted" style={{ opacity: 0.6 }}>
                    <CsLineIcons
                      icon="edit"
                      size="14"
                      className="flex-shrink-0 text-muted judge-edit-icon"
                    />
                  </span>
                </div>
              </Button>

              {showContestantPicker && (
                <div className="judge-contestant-picker-menu bg-foreground border rounded shadow-lg mt-2">
                  <div className="small text-muted fw-bold px-2 pt-2 pb-1">
                    Contestants in this age group
                  </div>

                  <div className="d-grid gap-1 p-2">
                    {contestants.map((contestant: any, index: number) => {
                      const isSelectedContestant = index === contestantIndex;
                      const status = getContestantAocStatus(contestant);

                      return (
                        <Button
                          key={`contestant-picker-${contestant.id}`}
                          variant={isSelectedContestant ? "primary" : "light"}
                          className="d-flex align-items-center justify-content-between gap-2 text-start p-2"
                          onClick={() => {
                            void proceedWithUnsavedChanges(() => {
                              setContestantIndex(index);
                              setViewMode("contestant");
                              setShowContestantPicker(false);
                            });
                          }}
                        >
                          <span className="fs-2 fw-bold lh-1 text-body">
                            #{contestant.contestantNumber || index + 1}{" "}
                            {getContestantShortName(contestant)}
                          </span>

                          <span className={`badge ${status.badgeClass} flex-shrink-0`}>
                            {status.label}
                          </span>
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="text-end flex-shrink-0">
              <div className="judge-filter-detail text-muted small">Total</div>
              <div className="fs-2 fw-bold lh-1">
                {formatTrimmedScore(totalScore)}
                <span>
                  <em>
                    {plusMarkCount > 0
                      ? ` (${"+".repeat(Math.min(plusMarkCount, 10))}${plusMarkCount > 10 ? `+${plusMarkCount - 10}` : ""
                      })`
                      : ""}
                  </em>
                </span>
              </div>
            </div>
          </div>

          <Row className="d-md-none mt-2 gap-1">
            {(selectedAocObj.aocScoreCategories ?? []).map((category: any) => {
              const categoryId = Number(category?.id ?? 0);
              const score = getCurrentScore(Number(selectedContestant.id), categoryId);

              return (
                <Col key={`mobile-header-category-${categoryId}`}>
                  <div className="text-muted text-small text-truncate">
                    {String(category?.scoringCategoryName ?? "Score")}
                  </div>
                  <strong>
                    {score !== null && score !== undefined
                      ? formatScoreButtonLabel(Number(score))
                      : "—"}
                  </strong>
                </Col>
              );
            })}
          </Row>
        </div>
      ) : viewMode === "ageGroupLanding" ? (
        <div className="bg-foreground rounded p-3">
          <div className="d-flex justify-content-between align-items-center gap-3">
            <div className="min-width-0">
              <div>
                <span className="cta-1 fw-bold text-body text-truncate">
                  {selectedAgeGroupProgress.completed}/{selectedAgeGroupProgress.total}
                </span>
                <span className="cta-4 fw-bold text-body text-truncate">Contestants</span>
              </div>

              <div className="small text-muted">Complete for {selectedAocObj?.name ?? "AOC"}  </div>
            </div>

            <div className="text-end flex-shrink-0">
              <div
                className={`fw-bold fs-2 ${selectedAgeGroupProgress.percent === 100
                    ? "text-success"
                    : "text-muted"
                  }`}
              >
                {selectedAgeGroupProgress.percent}%
              </div>

            </div>
          </div>
        </div>
      )
        : null}
    </div>
  );


  const content = (
    <div className="w-100 judge-scoring-content z-index-2">
      {isLoading && (
            <LoadingOverlay show={true} text={"Loading"} />
      )}

      {!selectedAgeGroup ? (
        <Card className="shadow-sm border-0">
          <Card.Body className="p-3 p-md-5">
            <div className="text-center mb-3">
              <h4 className="mb-2 text-body">Select an age group</h4>
              <p className="text-muted mb-0">
                Choose an age group to begin scoring.
              </p>
            </div>

            <Row className="row row-cols-2 g-2 g-md-3">
              {availableAgeGroups.map((ag: any) => {
                const counts = getAgeGroupCompletionCounts(ag);
                const total = counts.complete + counts.incomplete;
                const percent =
                  total === 0 ? 0 : Math.round((counts.complete / total) * 100);
                const isEmpty = total === 0;
                const isComplete = total > 0 && counts.incomplete === 0;

                return (
                  <Col key={ag.ageGroupId}>
                    <Button
                      variant={
                        isEmpty
                          ? "muted"
                          : isComplete
                            ? "success"
                            : "outline-primary"
                      }
                      className="w-100 h-100 text-start p-3"
                      onClick={() => {
                        void proceedWithUnsavedChanges(() => {
                          userManuallySelectedAgeGroupRef.current = true;
                          setSelectedAgeGroup(ag);
                          setContestantIndex(0);
                          setViewMode("ageGroupLanding");
                        });
                      }}
                    >
                      <div className="fw-bold text-truncate">
                        {ag?.ageGroupName}
                      </div>
                      <div className="small mt-1">
                        {isEmpty
                          ? "No Entries"
                          : `${percent}% complete · ${counts.complete}/${total}`}
                      </div>
                    </Button>
                  </Col>
                );
              })}
            </Row>
          </Card.Body>
        </Card>
      ) : viewMode === "ageGroupLanding" ? (
        <Card className="shadow-sm border-0">
          <Card.Body className="p-3 p-md-4">
            {contestants.length === 0 ? (
              <div className="border rounded bg-light p-4 text-center text-muted">
                No contestants found in this age group.
              </div>
            ) : (
              <Row className="g-2 g-md-3 row-cols-2 row-cols-md-3 row-cols-xl-4">
                {contestants.map((contestant: any, index: number) => (
                  <Col key={`age-group-landing-${contestant.id ?? index}`}>
                    <Button
                     variant="outline-body"
                      className="w-100 h-100 p-3 text-center"
                      onClick={() => {
                        setContestantIndex(index);
                        setShowContestantPicker(false);
                        setViewMode("contestant");
                      }}
                    >
                      <div className="display-6 fw-bold lh-1 mb-2">
                        #{contestant.contestantNumber || index + 1}
                      </div>
                      <div className="fw-bold text-truncate mb-2">
                        {getContestantShortName(contestant)}
                      </div>
                      {renderContestantStatusBadge(contestant)}
                    </Button>
                  </Col>
                ))}
              </Row>
            )}
          </Card.Body>
        </Card>
      ) : viewMode === "sideAwards" ? (
        <JudgeSideAwardWinners
          pageantId={Number((pageant as any)?.id ?? (pageant as any)?.pageantId ?? 0)}
          divisionId={Number(divisionId ?? 0)}
          ageGroup={selectedAgeGroup}
          contestants={sideAwardContestants}
          sideAwards={sideAwardsForSelectedAgeGroup}
          onBack={() => setViewMode("ageGroupLanding")}
          onSummaryChange={handleSideAwardSummaryChange}
          onSavingChange={setIsSavingSideAwardWinner}
onWinnerChange={async ({
  registeredPageantContestantId,
  pageantAvailableTitleId,
  relationship,
}) => {
  updateAwardRelationshipLocal(
    {
      registeredPageantContestantId,
      pageantAvailableTitleId,
    },
    relationship,
  );

  return relationship;
}}
        />
      ) : viewMode === "review" ? (
        reviewTable
      ) : !selectedContestant || !selectedAocObj ? (
        <Card className="shadow-sm border-0">
          <Card.Body className="p-5 text-center">
            <h4 className="mb-2">No contestants found</h4>
            <p className="text-muted mb-0">
              Choose an age group from the filters above to begin scoring.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Card className="shadow-sm border-0">
          <Card.Body className="p-3 position-relative">
            {!isContestantEnteredInAoc(selectedContestant) ? (
              <div className="judge-not-entered-watermark" aria-hidden="true" />
            ) : null}

            <div className="position-relative" style={{ zIndex: 1 }}>


              {(() => {
                const categories = selectedAocObj.aocScoreCategories ?? [];

                const renderScoreButtons = (
                  cat: any,
                  buttonClassName = "me-2 py-3",
                ) => {
                  const categoryId = Number(cat?.id ?? 0);
                  const fetchedScore = getFetchedScore(
                    selectedContestant.id,
                    categoryId,
                  );
                  const currentScore = getCurrentScore(
                    selectedContestant.id,
                    categoryId,
                  );
                  const options = buildScoreOptions(
                    Number(cat?.minScore ?? 0),
                    Number(cat?.maxScore ?? 0),
                  );

                  const hasFetched =
                    fetchedScore !== null && fetchedScore !== undefined;
                  const hasEdited =
                    updatedScores[selectedContestant.id]?.[categoryId] !==
                    undefined;
                  const maxPlusValue = Number(cat?.maxScore ?? 0) + 0.1;

                  const isOriginalPlus =
                    hasFetched &&
                    Math.abs(Number(fetchedScore) - maxPlusValue) < 0.0001;
                  const isCurrentPlus =
                    currentScore !== null &&
                    Math.abs(Number(currentScore) - maxPlusValue) < 0.0001;

                  let plusVariant: string = "outline-body";
                  let plusExtraClass = "";

                  if (hasEdited) {
                    if (isCurrentPlus) {
                      plusVariant = "danger";
                    } else if (isOriginalPlus) {
                      plusVariant = "light";
                      plusExtraClass = "bg-muted text-white";
                    }
                  } else if (isOriginalPlus) {
                    plusVariant = "info";
                  }

                  return (
                    <>
                      {options.map((value) => {
                        const isOriginalValue =
                          hasFetched &&
                          Math.abs(Number(fetchedScore) - value) < 0.0001;
                        const isCurrentValue =
                          currentScore !== null &&
                          Math.abs(Number(currentScore) - value) < 0.0001;

                        let variant: string = "outline-body";
                        let extraClass = "";

                        if (hasEdited) {
                          if (isCurrentValue) {
                            variant = "danger";
                          } else if (isOriginalValue) {
                            variant = "light";
                            extraClass = "bg-muted text-white";
                          }
                        } else if (isOriginalValue) {
                          variant = "info";
                        }

                        return (
                          <Button
                            key={`${categoryId}-${value}`}
                            variant={variant}
                            className={`${buttonClassName} ${extraClass}`}
                            onClick={() =>
                              handleScoreClick(
                                selectedContestant.id,
                                categoryId,
                                value,
                              )
                            }
                          >
                            {formatScoreButtonLabel(value)}
                          </Button>
                        );
                      })}

                      <Button
                        variant={plusVariant}
                        className={`${buttonClassName} ${plusExtraClass}`}
                        title="Bonus"
                        onClick={() =>
                          handleScoreClick(
                            selectedContestant.id,
                            categoryId,
                            maxPlusValue,
                          )
                        }
                      >
                        +
                      </Button>
                    </>
                  );
                };

                return (
                  <>

                    {getFeedbackVariantForAoc(selectedAocObj) === "photo" && (
                      <div className="border shadow-sm">
                        <div className="p- d-flex justify-content-end">
                          <span className="fw-bold p-2">Selected Photo</span>
                          <span className="d-flex gap-1">
                            {[1, 2, 3, 4, 5].map((photoNumber) => {
                              const feedbackKey = makeFeedbackKey(
                                Number(selectedContestant.id),
                                Number(selectedAOC),
                                Number(judgeId),
                              );

                              const isSelected =
                                feedbackDrafts[feedbackKey]?.selectedPhoto === photoNumber;

                              return (
                                <Button
                                  key={photoNumber}
                                  variant={isSelected ? "quaternary" : "outline-muted"}
                                  className="rounded-circle fw-bold"
                                  onClick={() => {
                                    setFeedbackDrafts((prev) => ({
                                      ...prev,
                                      [feedbackKey]: {
                                        ...(prev[feedbackKey] ?? {}),
                                        selectedPhoto: photoNumber,
                                      },
                                    }));
                                  }}
                                >
                                  {photoNumber}
                                </Button>
                              );
                            })}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="d-md-none d-grid gap-3">
                      {categories.map((cat: any) => {
                        const categoryId = Number(cat?.id ?? 0);
                        const currentScore = getCurrentScore(
                          selectedContestant.id,
                          categoryId,
                        );

                        return (
                          <Card key={categoryId} className="border shadow-sm">
                            <Card.Body className="p-3">
                              <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                                <div>
                                  <div className="small text-muted">
                                    Category
                                  </div>
                                  <div className="fw-bold fs-5">
                                    {cat.scoringCategoryName}
                                  </div>
                                </div>
                                <div className="text-end">
                                  <div className="small text-muted">
                                    Selected
                                  </div>
                                  <div className="fw-bold fs-5">
                                    {currentScore !== null &&
                                      currentScore !== undefined
                                      ? formatScoreButtonLabel(
                                        Number(currentScore),
                                      )
                                      : "—"}
                                  </div>
                                </div>
                              </div>
                              <div className="judge-score-card-options">
                                {renderScoreButtons(
                                  cat,
                                  "judge-score-card-button py-3",
                                )}
                              </div>
                            </Card.Body>
                          </Card>
                        );
                      })}
                    </div>

                    <div className="table-responsive d-none d-md-block">
                      <table className="table align-middle">
                        <thead>
                          <tr>
                            <th style={{ width: "100%" }}>Category</th>
                            <th style={{ whiteSpace: "nowrap" }}>Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {categories.map((cat: any) => {
                            const categoryId = Number(cat?.id ?? 0);

                            return (
                              <tr key={categoryId}>
                                <td className="fw-semibold">
                                  {cat.scoringCategoryName}
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  <div
                                    className="d-flex"
                                    style={{
                                      flexWrap: "nowrap",
                                      overflowX: "auto",
                                      WebkitOverflowScrolling: "touch",
                                    }}
                                  >
                                    {renderScoreButtons(cat)}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
              {(() => {
                const feedbackKey = makeFeedbackKey(
                  Number(selectedContestant.id),
                  Number(selectedAOC),
                  Number(judgeId),
                );

                const feedback = feedbackDrafts[feedbackKey] ?? {
                  excelledIn: [],
                  improveOn: [],
                  customNote: "",
                  deductionApplied: false,
                };

                const variant = getFeedbackVariantForAoc(selectedAocObj);
                const feedbackOptions = FEEDBACK[variant];

                const toggleItem = (
                  section: "excelledIn" | "improveOn",
                  item: string,
                ) => {
                  setFeedbackDrafts((prev) => {
                    const current = prev[feedbackKey] ?? {
                      excelledIn: [],
                      improveOn: [],
                      customNote: "",
                      deductionApplied: false,
                    };

                    const itemKey = canonicalFeedbackItem(item);
                    const otherSection =
                      section === "excelledIn" ? "improveOn" : "excelledIn";

                    const sectionHasItem = current[section].some(
                      (x) => canonicalFeedbackItem(x) === itemKey,
                    );

                    const nextSectionValues = sectionHasItem
                      ? current[section].filter(
                        (x) => canonicalFeedbackItem(x) !== itemKey,
                      )
                      : [...current[section], item];

                    const nextOtherSectionValues = current[otherSection].filter(
                      (x) => canonicalFeedbackItem(x) !== itemKey,
                    );

                    return {
                      ...prev,
                      [feedbackKey]: {
                        ...current,
                        [section]: nextSectionValues,
                        [otherSection]: nextOtherSectionValues,
                      },
                    };
                  });
                };

                return (
                  <Card className="border-1 p-2">
                    <Row>
                      <Col className="col-auto">
                        <div className="mb-3">
                          <div className="fw-bold mb-2  text-body">
                            Things you excelled in
                          </div>
                          <div className="d-flex flex-wrap gap-2">
                            {feedbackOptions.excel.map((item) => {
                              const isSelected = feedback.excelledIn.some(
                                (x) =>
                                  canonicalFeedbackItem(x) ===
                                  canonicalFeedbackItem(item),
                              );

                              const isBlocked = feedback.improveOn.some(
                                (x) =>
                                  canonicalFeedbackItem(x) ===
                                  canonicalFeedbackItem(item),
                              );

                              return (
                                <Button
                                  key={`excel-${item}`}
                                  size="sm"
                                  variant={
                                    isSelected ? "success" : "outline-body"
                                  }
                                  disabled={isBlocked && !isSelected}
                                  onClick={() => toggleItem("excelledIn", item)}
                                >
                                  {item}
                                </Button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="fw-bold mb-2 text-body">
                            Things you could improve on
                          </div>
                          <div className="d-flex flex-wrap gap-2">
                            {feedbackOptions.improve.map((item) => {
                              const isSelected = feedback.improveOn.some(
                                (x) =>
                                  canonicalFeedbackItem(x) ===
                                  canonicalFeedbackItem(item),
                              );

                              const isBlocked = feedback.excelledIn.some(
                                (x) =>
                                  canonicalFeedbackItem(x) ===
                                  canonicalFeedbackItem(item),
                              );

                              return (
                                <Button
                                  key={`improve-${item}`}
                                  size="sm"
                                  variant={
                                    isSelected ? "warning" : "outline-body"
                                  }
                                  disabled={isBlocked && !isSelected}
                                  onClick={() => toggleItem("improveOn", item)}
                                >
                                  {item}
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      </Col>
                      <Col>
                        <div>
                          <div className="d-flex justify-content-end">
                              <Button
    size="sm"
    variant="outline-danger"
 onClick={() => {
  void handleOpenFixMistake();
}}
  >
    Fix Mistake
  </Button>
      {/* {variant === "stage" && (
                            <div className="m-2">
                              <label className="d-flex align-items-center justify-content-end gap-2">
                                <input
                                  type="checkbox"
                                  checked={!!feedback.deductionApplied}
                                  onChange={(e) => {
                                    setFeedbackDrafts((prev) => {
                                      const current = prev[feedbackKey] ?? {
                                        excelledIn: [],
                                        improveOn: [],
                                        customNote: "",
                                        deductionApplied: false,
                                      };

                                      return {
                                        ...prev,
                                        [feedbackKey]: {
                                          ...current,
                                          deductionApplied: e.target.checked,
                                        },
                                      };
                                    });
                                  }}
                                />
                                <span className=" text-body">
                                  Deduction applied
                                </span>
                              </label>
                            </div>
                          )} */}
                          </div>
                      {hasTabletTestScoreOrNote && (
  <div className="alert alert-danger shadow-sm mb-4">
    <div className="fw-bold fs-4">TEST SCORES DETECTED</div>
    <div>
      Tablet test scores or test notes still exist. Delete/clear them before
      official judging begins.
    </div>
  </div>
)}
                          <div className="fw-bold mb-2  text-body">
                            Custom note
                          </div>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={feedback.customNote}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFeedbackDrafts((prev) => {
                                const current = prev[feedbackKey] ?? {
                                  excelledIn: [],
                                  improveOn: [],
                                  customNote: "",
                                  deductionApplied: false,
                                };

                                return {
                                  ...prev,
                                  [feedbackKey]: {
                                    ...current,
                                    customNote: value,
                                  },
                                };
                              });
                            }}
                            placeholder="Add any extra comments here..."
                          />
                        </div>
                      </Col>
                    </Row>
                  </Card>
                );
              })()}{" "}
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );

  const handleConfirmAndMoveToNextAgeGroup = useCallback(async () => {
    if (!selectedAgeGroup) return;

    if (!nextAgeGroup) {
      setDivisionCompleteModal(true);
      return;
    }

    const enteredContestants =
      getEnteredContestantsForAgeGroup(selectedAgeGroup);
    const incompleteContestants = enteredContestants.filter(
      (contestant: any) => !isContestantCompleteForSelectedAoc(contestant),
    );

    if (incompleteContestants.length > 0) {
      const shouldContinue = await showConfirmModal({
        title: "Age group incomplete",
        body: `This age group is not complete. ${incompleteContestants.length} entered contestant${incompleteContestants.length === 1 ? " is" : "s are"
          } still incomplete. Move to ${getAgeGroupName(nextAgeGroup)} anyway?`,
        confirmText: "Move Anyway",
        cancelText: "Stay Here",
        confirmVariant: "warning",
      });

      if (!shouldContinue) return;
    }

    await proceedWithUnsavedChanges(() => {
      suppressExactCompletePromptRef.current = true;
      setSelectedAgeGroup(nextAgeGroup);
      setContestantIndex(0);
      setViewMode("ageGroupLanding");
    });
  }, [
    selectedAgeGroup,
    nextAgeGroup,
    getEnteredContestantsForAgeGroup,
    isContestantCompleteForSelectedAoc,
    proceedWithUnsavedChanges,
    showConfirmModal,
  ]);

  const bottomFixedControls = selectedAgeGroup ? (
    viewMode === "sideAwards" ? (
      <div className="judge-fixed-footer border-top shadow-lg z-index-1">
        <div className="container-fluid py-2">
          <div className="d-flex align-items-center justify-content-end w-100 gap-2">

            {/* <Button
              variant="info"
              className="cta-1 fs-3 fw-bold w-100"
              disabled={isSavingSideAwardWinner}
              onClick={() => {
                if (isSavingSideAwardWinner) return;
                setViewMode("ageGroupLanding");
              }}>
              {isSavingSideAwardWinner ? "Side Awards Saving..." : "Finish Side Awards"}
            </Button> */}

            <Button
  variant="info"
  className="cta-1 fs-3 fw-bold w-100"
  disabled={isSavingSideAwardWinner}
  onClick={() =>
    void handleFinishSideAwards()
  }
>
  {isSavingSideAwardWinner
    ? "Checking Side Awards..."
    : "Finish Side Awards"}
</Button>
          </div>
        </div>
      </div>
    ) : viewMode === "ageGroupLanding" ? (
      <div className="judge-fixed-footer border-top shadow-lg z-index-1">
  <Row className="p-2 row-cols-1 row-cols-md-2">
  <Col className="mb-1">
  <div className="d-flex gap-2">
    <Button
      variant="outline-primary"
      className="cta-3 fs-3"
      onClick={() => history.push(judgeHomePath)}
    >
      <CsLineIcons icon="home" />
    </Button>
     <Button
      variant="outline-secondary"
      className="w-100"
      onClick={() => setViewMode("sideAwards")}
    >
      Judge Side Awards
    </Button>
    </div>
  </Col>


  <Col>
    <Button
      variant="info"
      className="w-100 cta-3 fs-3 fw-bold"
      onClick={() => setViewMode("contestant")}
    >
      Begin Scoring
    </Button>
  </Col>
</Row>
      </div>
    ) : (
   <div className="judge-fixed-footer bg-foreground border-top shadow-lg z-index-1">
  <div className="container-fluid py-2">
    <div className="judge-scoring-footer-layout">

      {/* MOBILE / DESKTOP NAV + TIMELINE */}
      <div className="judge-footer-mobile-nav">

        {/* PREVIOUS */}
 <Button
  variant="outline-secondary"
  className="judge-icon-action"
  disabled={false}
  title="Previous"
  onClick={() => {
    void proceedWithUnsavedChanges(
      () => {
        if (viewMode === "review") {
          setContestantIndex(Math.max(contestants.length - 1, 0));
          setViewMode("contestant");
          return;
        }

        if (contestantIndex === 0) {
          setViewMode("ageGroupLanding");
          return;
        }

        setContestantIndex((prev) => Math.max(prev - 1, 0));
        setViewMode("contestant");
        scrollScoringViewToTop();
      },
      { autoSave: true },
    );
  }}
>
  <CsLineIcons icon="chevron-left" size="20" />
</Button>
        {/* TIMELINE */}
        <div className="judge-footer-timeline-wrap">
          <div className="judge-contestant-timeline">
            <div className="judge-contestant-bubbles">

              {/* LANDING */}
              <Button
                variant="outline-muted"
                className="judge-contestant-bubble rounded-circle judge-footer-mode-bubble"
                title="Contestants landing"
                onClick={() => {
                  void proceedWithUnsavedChanges(() => {
                    setViewMode("ageGroupLanding");
                  });
                }}
              >
                <CsLineIcons icon="user" size="14" />
              </Button>

              {/* CONTESTANTS */}
              {contestants.map((contestant: any, index: number) => {
                const isSelected =
                  viewMode === "contestant" && index === contestantIndex;

                const isEntered = isContestantEnteredInAoc(contestant);

                const isComplete =
                  isContestantCompleteForSelectedAoc(contestant);

                let variant: string = "outline-primary";
                let extraClass = "";

                if (!isEntered) {
                  variant = "light";
                  extraClass = "bg-muted text-white border-muted";
                } else if (isComplete) {
                  variant = "success";
                }

                return (
                  <Button
                    key={contestant.id}
                    variant={isSelected ? "primary" : variant}
                    active={isSelected}
                    className={`judge-contestant-bubble rounded-circle ${
                      isSelected
                        ? "active"
                        : "judge-contestant-bubble-inactive"
                    } ${extraClass}`}
                    onClick={() => {
                      void proceedWithUnsavedChanges(() => {
                        setContestantIndex(index);
                        setViewMode("contestant");
                      });
                    }}
                  >
                    <span className="judge-contestant-bubble-label">
                      {contestant.contestantNumber || index + 1}
                    </span>
                  </Button>
                );
              })}

              {/* REVIEW */}
              <Button
                variant={
                  viewMode === "review"
                    ? "warning"
                    : "outline-warning"
                }
                active={viewMode === "review"}
                className={`judge-contestant-bubble rounded-circle judge-footer-mode-bubble ${
                  viewMode === "review" ? "active" : ""
                }`}
                title="Review"
                onClick={() => {
                  void proceedWithUnsavedChanges(() =>
                    setViewMode("review"),
                  );
                }}
              >
                <CsLineIcons icon="search" size="14" />
              </Button>
            </div>
          </div>
        </div>


        {/* NEXT */}
<Button
  variant="outline-secondary"
  className="judge-icon-action"
  disabled={viewMode === "review"}
  title="Next"
  onClick={() => {
    void proceedWithUnsavedChanges(
      () => {
        if (contestantIndex >= contestants.length - 1) {
          setViewMode("review");
          return;
        }

        setContestantIndex((prev) =>
          Math.min(prev + 1, contestants.length - 1),
        );
        setViewMode("contestant");
        scrollScoringViewToTop();
      },
      { autoSave: true },
    );
  }}
>
  <CsLineIcons icon="chevron-right" size="20" />
</Button>
      </div>

      {/* ACTIONS */}
      <div className="judge-footer-actions">

 {hasScoreEdits && (
  <Button
    variant="outline-danger"
    className="judge-icon-action judge-footer-undo-action flex-shrink-0"
    onClick={handleUndoChanges}
    title="Undo changes"
  >
    Undo
  </Button>
)}

        {viewMode === "review" ? (
          <Button
            variant="success"
            className="cta-3 fs-3 fw-bold judge-footer-primary-action"
            onClick={() => {
              void handleConfirmAndMoveToNextAgeGroup();
            }}
            disabled={false}
          >
            {nextAgeGroup
              ? `Continue to ${getAgeGroupName(nextAgeGroup)}`
              : "Continue"}
          </Button>
        ) : (
          <Button
            variant="success"
            className="cta-3 fs-3 fw-bold judge-footer-primary-action"
            onClick={handleSaveAndNext}
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : !hasScoreEdits
                ? isLastContestant
                  ? "Review All"
                  : "Next"
                : isLastContestant
                  ? "Save and Complete"
                  : "Save + Next"}
          </Button>
        )}
      </div>
    </div>
  </div>
</div>
    )
  ) : null;



  return (
    <LayoutJudgePortal
      judgeView="scoring"
      headerState={isHeaderCondensed ? 'condensed' : 'expanded'}
    >
      {/* {fixedScoringStyles} */}

      <div
        className={`judge-sticky-scoring-header bg-primary p-2 w-100 shadow-sm z-index-1 ${isHeaderCondensed ? "is-condensed" : ""}`}>
        {scoringFilters}
        {filterSelectionDropdown}
        {scoringHeader}
      </div>

      {content}
      {bottomFixedControls}
      {pickerModal}
      {savingModal}
      {confirmUiModal}
      {fixMistakeUiModal}
      {missingScoreUiModal}
      {/* completionUiModal intentionally disabled */}
      {divisionCompleteUiModal}
    </LayoutJudgePortal>
  );
};

export default JudgeScoringPlatform;
